import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useRef,
  useMemo,
  useState,
} from "react";
import {
  manualSpinStop,
  rgsPlay,
  setSpinComplete,
  setReelsFrozen,
  decrementRemainingAutoSpins,
  stopAutoPlay,
  pauseAutoPlayForFeature,
  clearAutoplayPausedSpins,
  setAutoplaySpeed,
  applyPendingAutoplaySpeed,
  incrementManualStopCount,
  setTurboSuggestionPending,
  setStopOnAnyWin,
  setStopOnBonusGame,
  setStopOnSingleWinExceeds,
  setStopOnBalanceIncreases,
  setStopOnBalanceDecreases,
  setSingleWinExceedsAmount,
  setBalanceIncreasesAmount,
  setBalanceDecreasesAmount,
  openConnectionPopup,
  closeConnectionPopup,
  setCounterAnimationFinished,
  setCounterAnimationStarted,
  setResetMainGameVisualEffects,
  clearPotCollectionWin,
  setPotCollectionOutroCounterFinished,
  openReplayEnded,
  openReplay,
  setEndedRound,
  setUserInterfaceDisabled,
  setCriticalAnimationPlaying,
} from "../State/actions";
import {
  selectAutoPlaySpeed,
  selectAutoPlayActive,
  selectRemainingAutoSpins,
  selectPendingAutoPlaySpeed,
  selectAutoSpinCount,
  selectStopOnAnyWin,
  selectStopOnBonusGame,
  selectStopOnSingleWinExceeds,
  selectStopOnBalanceIncreases,
  selectStopOnBalanceDecreases,
  selectSingleWinExceedsAmount,
  selectBalanceIncreasesAmount,
  selectBalanceDecreasesAmount,
  selectCurrentRound,
  selectIsSpinning,
  selectManualStop,
  selectSpinComplete,
  selectMenuActive,
  selectBalance,
  selectBet,
  selectCounterAnimationFinished,
  selectCounterAnimationStarted,
  selectWinAnimationsPlaying,
  selectReplaySessionActive,
  selectReplayFinished,
  selectGameStatus,
  selectErrorCode,
  selectErrorPopupActive,
  selectInfoPopupActive,
  selectFreeRoundsCompletionPending,
  selectHoldAndWinActive,
  selectPotCollectionTriggered,
  selectPotCollectionActive,
  selectCriticalAnimationPlaying,
  selectPotCollectionOutroTransitionActive,
  selectAnyFeatureTriggeredOrActive,
  selectFreeSpinsActive,
  selectFreeSpinsTriggered,
  selectShowFreeSpinTotalWin,
  selectShowGambleDialog,
  selectShowFreeSpinIntro,
  selectShowFreeSpinMaxWin,
  selectShowFreeSpinZeroWin,
  selectShowTransitionDialog,
} from "../State/selectors";
import { useDispatch, useSelector } from "react-redux";
import store from "../State/Store/store";
import { useInitialIdleAnimations } from "../Hooks/useLandingAnimations";

import { FEATURES_CONFIG } from "../featuresConfig";
import { baseDiag } from "../utils/fsDiag";
import { CONNECTION_POPUP_TIMEOUTS } from "../Components/UserInterface/primary/Popups/ConnectionPopup";
import { SOUND_NAME } from "../sounds";
import { useAppActivity } from "./AppActivityContext";
import {
  parseAutoplayInputFromStore,
  createPlayButtonRotationAnimation,
} from "../utilities";
import { Settings } from "../settings";
import { triggerScalePress } from "../Hooks/useButtonHover";
import { useReelRefs } from "./ReelRefsContext";
import { soundManager } from "../SoundManager";
import { COIN_SYMBOL_IDS, BONUS_SYMBOL_IDS } from "../SymbolData";
import { PROVIDER_CONFIG } from "../RGS/activeProviderConfig";
import { isTurboLike, isSuperTurbo } from "../utils/speedHelpers";
import * as PIXI from "pixi.js";
import TickerTimeout from "../utils/TickerTimeout";
import { waitForBoardToSettle } from "../Games/Shared/waitForBoardToSettle";
import { logoPotWin } from "../Games/Shared/logoPot";
import { beginRandomPotFlight } from "../Games/Shared/randomPot";
import { selectIntroPopupActive } from "../State/Slices/userInterfaceSlice";
import { selectShowGameAfterTransition } from "../State/Slices/animationsSlice";
import { createAnticipationRunner } from "../Games/Shared/anticipationRunner";
import { createTensionSymbols } from "../Games/Shared/tensionSymbols";
import { anticipationChain } from "../Games/Shared/scatterAnticipation";
// NOT USED since the per-reel scatter anticipation replaced the Mad Labs glow:
// import {
//   SCENE_ACCENTS,
//   SCENE_ACCENT_TRACKS,
// } from "../Games/Shared/sceneAccents";
import useFreeRoundsCompletion from "../Hooks/useFreeRoundsCompletion";
import useRevealSkip from "../Hooks/useRevealSkip";

// The symbols a tension keeps lit (they are what the anticipation waits for):
// the Fireball coins (plain and jackpot) and the scatter.
const isTensionTriggerSymbol = (id) =>
  COIN_SYMBOL_IDS.includes(Number(id)) ||
  Number(id) === PROVIDER_CONFIG.FREE_SPINS_TRIGGER?.SCATTER_ID;


/**
 * Context for managing reel control functionality.
 * Handles manual spins, auto-play mode, spacebar turbo mode, and tension animations.
 */
export const ReelControlContext = createContext(null);

// How long after a replayed round settles before the summary popup opens.
// The effect's own gate already waits for the spin, the win animations and the
// whole bonus outro, so this is only a breath between the board going quiet and
// the popup arriving — not a safety margin. Kept short; the game is quick.
const REPLAY_ENDED_POPUP_DELAY_MS = 500;

/**
 * Detects if the current round is a Hold & Win trigger (6+ coins).
 * Used to suppress landing animations in turbo mode even when there's no win amount.
 */
const detectHoldAndWinTrigger = (round) => {
  if (!round?.reels || !Array.isArray(round.reels)) return false;

  let coinCount = 0;

  for (let reelIndex = 0; reelIndex < round.reels.length; reelIndex++) {
    const reel = round.reels[reelIndex];
    if (!Array.isArray(reel)) continue;

    for (let symbolIndex = 0; symbolIndex < reel.length; symbolIndex++) {
      const symbolId = reel[symbolIndex];
      if (COIN_SYMBOL_IDS.includes(symbolId)) {
        coinCount++;
      }
    }
  }

  return coinCount >= 6;
};

/**
 * The total win of the round just replayed, for the standalone replay link's
 * Ended modal (docs/replay-history-spec.md §0 Stage 9/12).
 *
 * The in-game path reads this off the history row it launched from; a
 * standalone page has no such row, so it has to come from the session.
 * `replayRoundWin` is the round's own cumulative win, recorded from each
 * step's `balance.game` — authoritative for BOTH base and bonus rounds.
 *
 * Deliberately NOT replayLastAppliedWin, which looks equivalent but is a
 * credit ledger: free-rounds completion pushes the whole CAMPAIGN total
 * through it, so a replayed final free round would report the campaign's
 * winnings as this round's win (and a wildly wrong xBet).
 *
 * state.game.currentRound is only a fallback: gameSettingsSlice stops
 * writing it for feature spins, so a replayed bonus round would report 0
 * from it. A genuinely zero-win round correctly yields 0 either way.
 */
const resolveStandaloneRoundWin = (state) =>
  state?.replay?.replayRoundWin || state?.game?.currentRound?.win || 0;

/**
 * Has a finished replay round come fully to rest?
 *
 * A round's last STEP is not its end. A bonus keeps going through its respins,
 * outro and the cloud transition back to base game — and is only paid out at
 * the far side of that. A campaign's final free round then runs its own
 * completion sequence (info popup → 206 notice → teardown revealing the Total
 * Win). The Ended summary must not cover any of it.
 *
 * Shared by the auto-open effect and handleSpin's backstop so the two can't
 * drift apart — they gate the same moment and previously duplicated this list.
 */
/**
 * Is a PRESENTATION still holding the screen?
 *
 * The one test every deferred spin has to repeat. Both auto-drivers decide to
 * spin, then wait a delay and fire — turbo 150ms, super turbo 75ms, autoplay
 * in turbo 16ms — and the decision and the firing are different moments. The
 * mystery reveal raises criticalAnimationPlaying in between, from an effect
 * that runs AFTER these do on the same spinComplete tick, so a guard at the
 * decision alone lets the spin through and it lands inside the reveal.
 *
 * criticalAnimationPlaying covers the reveal, which pays nothing and therefore
 * raises no counter and no win flag — it is the only thing that speaks for it.
 * The feature selector covers a trigger the moment the response lands.
 */
const isPresentationHoldingScreen = (state) =>
  state?.animations?.criticalAnimationPlaying === true ||
  // A counter still counting. handleSpin REFUSES on exactly this test, so
  // without it here autoplay decrements its own counter and then calls a spin
  // that silently declines — and because remainingAutoSpins is in the effect's
  // deps, the decrement re-runs the effect and it does it again. Measured
  // live: one stranded trigger round burned 69 autoplay spins down to 16
  // without a single request leaving the client.
  (state?.animations?.counterAnimationStarted === true &&
    state?.animations?.counterAnimationFinished !== true) ||
  selectAnyFeatureTriggeredOrActive(state);

/**
 * Is the free-spins TRIGGER still a reason to hold the summary?
 *
 * `triggered` means "a package was won and the room has not taken the screen
 * yet". It is cleared in exactly three places: the room opening
 * (setFreeSpinsActive(true)), a gamble that LOST, and endReplaySession putting
 * the pre-replay snapshot back.
 *
 * ── The deadlock ──
 *
 * The free spins are their own ROUNDS in history. So replaying the base round
 * that WON them plays the scatters, the gamble, the intro — and then stops,
 * because there are no more steps. `triggered` stays true. Nothing is coming
 * to clear it: the room is not part of this round, and endReplaySession only
 * runs when the player closes the History board.
 *
 * Which they cannot do, because the board is opened BY the summary this gate
 * is holding shut. Replay ends → no summary → no board → no endReplaySession →
 * `triggered` never clears → the play button stays dead and the History list
 * never comes back. Reported 2026-09-18.
 *
 * A lost gamble clears `triggered` itself, which is why the same replay is
 * fine when the package was forfeit — the fault only shows on a round that
 * actually won something.
 *
 * Zeus cannot reach this: its bonus respins are part of the SAME round, so the
 * feature always runs and always clears its own flag.
 *
 * ── The rule ──
 *
 * A flag only a feature can clear must not hold a replay the feature will
 * never run in. So once the replay's steps are exhausted, `triggered` holds
 * ONLY while something of the trigger is still on screen — the gamble machine
 * or the intro plate. Both of those are real reasons to wait, and they are
 * what the clause was added for: the summary used to open on top of the Total
 * Win (round 1455068). Neither survives the player dismissing them, and that
 * is the moment this releases.
 *
 * Outside a replay `replayFinished` is false, so this is exactly the old test.
 */
const freeSpinsTriggerHolding = (state) => {
  if (state?.freeSpins?.triggered !== true) return false;
  if (state?.replay?.replayFinished !== true) return true;
  return (
    state?.animations?.showGambleDialog === true ||
    state?.animations?.showFreeSpinIntro === true
  );
};

const isReplayRoundSettled = (state) =>
  !state?.potCollection?.potCollectionActive &&
  !state?.potCollection?.potCollectionTriggered &&
  !state?.animations?.potCollectionOutroTransitionActive &&
  !state?.animations?.criticalAnimationPlaying &&
  !state?.freeRounds?.completionPending &&
  !state?.userInterface?.infoPopupActive &&
  !state?.userInterface?.errorPopupActive &&
  // ── The free-spins room, added late and for the same reason as the rest ──
  //
  // This predicate was written when replay knew only Pot Collection, and the
  // free-spins feature was never added to it. So a replayed free-spins round
  // reached its last step, the Ended modal's timer started immediately, and it
  // opened ON TOP of the Total Win dialog: the round's summary was never seen.
  // Observed on round 1455068 — the log shows `totalWinDialog -> startSequence
  // totalWin=288` and then the replay outro over it.
  //
  // `active` covers the room itself and `triggered` the window between the
  // trigger step and the room opening — the last step of a round can be either.
  // The three dialogs are listed separately because the room hands over to them
  // as it closes, so `active` alone drops before the player has seen the total.
  //
  // EVERY flag here must also be a dep of the effect below. This function reads
  // the store directly, so nothing re-runs the effect on its own — the deps are
  // what wake it when the last of these clears. Miss one and the modal never
  // opens at all.
  !state?.freeSpins?.active &&
  !freeSpinsTriggerHolding(state) &&
  !state?.animations?.showFreeSpinTotalWin &&
  !state?.animations?.showFreeSpinMaxWin &&
  !state?.animations?.showFreeSpinZeroWin &&
  // ── The WIPE, which is the last thing to leave the screen ──
  //
  // Pot Collection's outro transition was here from the start; the free-spins
  // one never was. They are not the same flag: this room leaves through
  // TransitionDialog, driven by animations.showTransitionDialog.
  //
  // Every flag above drops BEFORE the wipe finishes — the room resets and the
  // base game takes the screen back UNDER the cover, which is the whole point
  // of a wipe. So the round read as settled while the clouds were still
  // closing, and the Ended modal faded in over a moving screen. Confirmed from
  // the trace on round 1455673: `loop BLOCKED by transitionActive`, then
  // `resetFreeSpins`, then `ended modal ARMED` — armed mid-wipe.
  !state?.animations?.showTransitionDialog;

export const ReelControlProvider = ({ children }) => {
  const { triggerInitialIdleAnimations } = useInitialIdleAnimations();
  const { reelRefs, overlayReelRefs, tensionReelRefs, playButtonRef, gameContainerRef, characterAnimationControllerRef, dimReelSetRef, snapClearAllRef, gameViewControllerRef } =
    useReelRefs();

  // Slam stop for any spin in flight — tap or spacebar, GDD §13. Self
  // contained: it reads the skip registry directly and attaches its listeners
  // only while something is actually spinning. Mounted here because this
  // provider is alive for the whole session.
  useRevealSkip();
  const dispatch = useDispatch();
  const { isActive } = useAppActivity();


  // ============================================================================
  // REFS & STATE MANAGEMENT
  // ============================================================================

  // Rate limiting for effects
  const lastActionRef = useRef({
    turboActivation: 0,
    turboContinuous: 0,
  });

  // Timeout tracking and spin state
  const timeoutsRef = useRef([]);
  const reelStopTimeoutsRef = useRef(new Map()); // Map<reelIndex, timeoutId> for tracking reel-specific timeouts
  const stopTriggeredRef = useRef(false);
  const pendingManualStopRef = useRef(false);
  const currentSpinMethodRef = useRef(null); // "turbo" | "regular" | null
  const stoppedReelsRef = useRef(new Set());
  const lastForceStoppedReelRef = useRef(-1); // Prevents duplicate force-stops on same reel
  const spinStartTimeRef = useRef(null);


  // Connection timeout management for slow server detection
  const connectionTimeouts = useRef({
    main: null,
    manualStop: null,
  });

  // Tension mode state (trigger symbol-based slow spin for anticipation)
  const tensionReels = useRef(new Set());
  const pendingTensionRef = useRef(null);
  // The per-reel scatter anticipation chain of the CURRENT round — see
  // stopReels. A ref, not state: it is read and written inside reel landing
  // callbacks and timeouts, which are not React's to re-render for. Keyed by
  // round id, so a manual-stop / tab-resume re-entry into stopReels picks the
  // running chain back up instead of starting it again.
  const anticipationChainRef = useRef(null);
  // // The reels the tension dim stripped to their pictures — a miss gives
  // // their idles back (onChainEnd).
  // const strippedReelsRef = useRef(new Set());
  // The tension's hold on the stopped reels' spines (wait clips, darkening).
  const tensionSymbolsRef = useRef(null);
  if (!tensionSymbolsRef.current) {
    tensionSymbolsRef.current = createTensionSymbols({
      getReelCtrl: (i) => reelRefs.current[i]?.getReelController?.(),
      getOverlayCtrl: (i) =>
        overlayReelRefs.current?.[i]?.getOverlayReelController?.(),
      isTrigger: isTensionTriggerSymbol,
      // Per chain, only the feature it is waiting for (arm):
      isCoin: (id) => COIN_SYMBOL_IDS.includes(Number(id)),
      isScatter: (id) =>
        Number(id) === PROVIDER_CONFIG.FREE_SPINS_TRIGGER?.SCATTER_ID,
      // The wait clips only — no darkening, no landing gate (2026-09-30).
      waitsOnly: true,
    });
  }
  // A chain reel is on its turn (in / loop / out) — a press then skips that
  // reel only, like the Zeus tension skip. See handleManualStop.
  const isAnticipationChainActive = () => {
    const chain = anticipationChainRef.current;
    return !!chain && !chain.done && chain.step >= 0;
  };
  // This room's wiring for the shared chain (Games/Shared/anticipationRunner).
  // Built fresh where needed — the chain state lives in the ref, so every
  // runner sees the same chain. Chain stops are tracked like every other stop,
  // so a new spin's clearAllTimeouts takes them down.
  const makeAnticipationRunner = (normalGapMs) =>
    createAnticipationRunner({
      chainRef: anticipationChainRef,
      getReels: () => reelRefs.current,
      getScene: () => gameViewControllerRef?.current,
      getDim: () => dimReelSetRef.current,
      // ── The tension dim (Vladimir, 2026-09-29) ── every reel but the
      // anticipating one dims; a stopped reel keeps only its coins and
      // scatters animated (they glow above the dim), everything else goes
      // back to its picture under it. A miss brings the idles back; a hit
      // ends on the full board dim, as any win does.
      // tensionDim: true,
      // ── OFF (Vladimir, 2026-09-30: "completely revert this reel dimming and
      // animation suppressing") ── back to the original chain: the reels
      // waiting their turn dim (SCATTER_ANTICIPATION.DIM_REELS), nothing is
      // stripped or darkened. The trigger symbols still play their wait clips
      // (waitReel below).
      tensionDim: false,
      waitReel: (reelIndex) => tensionSymbolsRef.current.darkenReel(reelIndex),
      // stripReel: (reelIndex) => {
        // const reelCtrl = reelRefs.current[reelIndex]?.getReelController?.();
        // const overlayCtrl =
          // overlayReelRefs.current?.[reelIndex]?.getOverlayReelController?.();
        // if (!reelCtrl || !overlayCtrl) return;
        // strippedReelsRef.current.add(reelIndex);
        // const cells = reelCtrl.symbolsContainer?.children || [];
        // const stripped = [];
        // cells.forEach((cell, row) => {
          // const id = cell?.currentSymbolId ?? cell?.symbolId;
          // if (isTensionTriggerSymbol(id)) return;
          // overlayCtrl.hideWinAnimation?.(row);
          // reelCtrl.showSymbolTexture?.(row);
          // stripped.push(row);
        // });
        // // ── The picture must survive the landing's own late hide ── a reel
        // // that is landing hides each cell's picture ONE frame after its
        // // landing spine starts (useLandingAnimations' textureHideOps), so a
        // // strip in that frame was undone: no spine, no picture, an empty
        // // cell (Vladimir, 2026-09-29). Put the picture back on the next two
        // // frames wherever the cell still has no animation.
        // if (!stripped.length) return;
        // let frames = 0;
        // const recheck = () => {
          // stripped.forEach((row) => {
            // if (!overlayCtrl.activeAnimations?.has(row)) reelCtrl.showSymbolTexture?.(row);
          // });
          // if (++frames >= 2) PIXI.Ticker.shared.remove(recheck);
        // };
        // PIXI.Ticker.shared.add(recheck);
      // },
      // // onChainEnd: (hit) => {
      // //   if (!hit) triggerInitialIdleAnimations(false);
      // // },
      // // ── A miss brings the idles back on the STRIPPED reels only ── the
      // // chain ends at the last chain reel's stop order, while that reel (and
      // // any after it) is still moving: the whole-board idle stamped spines on
      // // the symbols passing by and broke its landing (Vladimir, 2026-09-30:
      // // "last one just shows random spines on top of everything"). A reel
      // // still spinning gets its idle from its own landing. Same calls as
      // // triggerInitialIdleAnimations' default branch (useLandingAnimations).
      // onChainEnd: (hit) => {
        // const reels = [...strippedReelsRef.current];
        // strippedReelsRef.current.clear();
        // if (hit) return;
        // reels.forEach((reelIndex) => {
          // const reelCtrl = reelRefs.current[reelIndex]?.getReelController?.();
          // const overlayCtrl =
            // overlayReelRefs.current?.[reelIndex]?.getOverlayReelController?.();
          // if (!reelCtrl?.hasStopped || !overlayCtrl) return;
          // const cells = reelCtrl.symbolsContainer?.children || [];
          // cells.forEach((cell, row) => {
            // const id = cell?.currentSymbolId ?? cell?.symbolId;
            // if (id === undefined || isTensionTriggerSymbol(id)) return;
            // if (cell?.hasStaticOverride) return;
            // if (!overlayCtrl.getAvailableAnimations?.(row, id)?.hasIdle) return;
            // overlayCtrl.showIdleAnimation(row, id);
            // requestAnimationFrame(() => reelCtrl.hideSymbolTexture?.(row));
          // });
        // });
      // },
      // ── The stopped reels' symbols (Vladimir, 2026-09-30: "it must be
      // smooth") ── no texture swaps any more: coins and scatters play their
      // wait clips, every other spine darkens with its dim column and freezes
      // at its loop seam. A miss fades them back; a hit hands over to the
      // board dim. See Games/Shared/tensionSymbols.
      stripReel: (reelIndex) => tensionSymbolsRef.current.darkenReel(reelIndex),
      onChainEnd: (hit) => tensionSymbolsRef.current.finish(hit),
      onRoundReset: () => tensionSymbolsRef.current.reset(),
      // onChainStart: (fromReel) => tensionSymbolsRef.current.arm(fromReel),
      onChainStart: (fromReel, features) =>
        tensionSymbolsRef.current.arm(fromReel, features),
      // ── The reel must have LANDED first ── the chain starts on its landing
      // PEAK (the bounce), which comes BEFORE the reel stops and starts its
      // landing clips — so asking "is it still landing?" there found nothing
      // and the dim covered the landing (Vladimir, 2026-09-29: "reel before
      // anticipation must play land already"). So: wait for the reel to
      // report it has stopped (its landing clips start in that same call —
      // ReelControllerV2._finalizeLanding), then for those clips to finish
      // (their complete event; waitForBoardToSettle's stall guard). Checked
      // on the ticker, not timed.
      // whenReelSettled: (reelIndex) =>
      //   waitForBoardToSettle([
      //     overlayReelRefs.current?.[reelIndex]?.getOverlayReelController?.(),
      //   ]),
      // The landing SPINES have started: the reel reports it has stopped, and
      // its landing clips start in that same call (ReelControllerV2.
      // _finalizeLanding -> onReelStop). Checked on the ticker, not timed; a
      // reel that never stops (the round moved on) lets go after ~10 s.
      whenReelLanding: (reelIndex) =>
        new Promise((resolve) => {
          let frames = 0;
          const tick = () => {
            const reelCtrl = reelRefs.current[reelIndex]?.getReelController?.();
            if (reelCtrl && !reelCtrl.hasStopped && ++frames < 600) return;
            PIXI.Ticker.shared.remove(tick);
            resolve();
          };
          PIXI.Ticker.shared.add(tick);
        }),
      // Those landing clips have finished (their complete event, with
      // waitForBoardToSettle's stall guard).
      whenReelSettled: (reelIndex) =>
        waitForBoardToSettle([
          overlayReelRefs.current?.[reelIndex]?.getOverlayReelController?.(),
        ]),
      stopReel: (reelIndex) => {
        reelRefs.current[reelIndex]?.stopSpin();
        stoppedReelsRef.current.add(reelIndex);
      },
      track: (timeout, reelIndex) => {
        timeoutsRef.current.push(timeout);
        if (reelIndex !== null) reelStopTimeoutsRef.current.set(reelIndex, timeout);
      },
      untrack: (timeout, reelIndex) => {
        const idx = timeoutsRef.current.indexOf(timeout);
        if (idx > -1) timeoutsRef.current.splice(idx, 1);
        if (
          reelIndex !== null &&
          reelStopTimeoutsRef.current.get(reelIndex) === timeout
        ) {
          reelStopTimeoutsRef.current.delete(reelIndex);
        }
      },
      ...(normalGapMs !== undefined ? { normalGapMs } : {}),
    });
  const activeTensionAnimationsRef = useRef(new Set());
  const tensionAnimationStartTimesRef = useRef(new Map());
  const dimmedReelsRef = useRef(new Set()); // Track which reels are currently dimmed
  const tensionEffectActiveRef = useRef(false); // Track if tension effect animation is currently playing

  // Turbo mode state (spacebar-activated)
  const [spacebarTurboActive, _setSpacebarTurboActive] = useState(false);
  // REF MIRROR: Always reflects the latest value of spacebarTurboActive *synchronously*,
  // without waiting for React's render cycle. This is critical for stopSpacebarTurbo
  // which can fire from a keyup event before React commits the state update from
  // startSpacebarTurbo's setSpacebarTurboActive(true) call.
  const spacebarTurboActiveRef = useRef(false);
  const setSpacebarTurboActive = useCallback((value) => {
    spacebarTurboActiveRef.current = value;
    _setSpacebarTurboActive(value);
  }, []);
  const turboPendingStopRef = useRef(false);
  // ── Turbo: did the reels actually start TURNING? ──
  //
  // False means the press is still inside its wait window and the round is
  // going to be a plain DROP, which is what turbo is on a healthy connection.
  // True means the server missed the window, the reels came alive to cover it,
  // and the round now stops the way auto does.
  const turboSpinningRef = useRef(false);
  const turboSpinTimerRef = useRef(null);
  const turboPendingActivationRef = useRef(false);
  const previousSpeedRef = useRef("auto");
  const turboDeactivatingRef = useRef(false);
  const turboTensionEjectedRef = useRef(false); // True when turbo was ejected for tension — blocks held spacebar from skipping

  // Autoplay state management
  const autoplayProcessingRef = useRef(false);
  const autoplayInitializedRef = useRef(false);
  const pausedByInactivityRef = useRef(false);
  const lastTabReturnTimeRef = useRef(null); // Track last tab return to prevent rapid calls
  const responsibleGamblingCheckedRef = useRef(false);
  const autoplayStartBalanceRef = useRef(null); // Store balance when autoplay starts

  // Animation optimization flags
  const suppressLandingAnimationsRef = useRef(false);
  const suppressLandingAnimationsPerReelRef = useRef(new Set());
  const manualStopTargetReelsRef = useRef(new Set());

  // Cleanup guards
  const cleanupInProgressRef = useRef(false);
  const isSubscribedRef = useRef(false);

  // ============================================================================
  // UTILITY FUNCTIONS
  // ============================================================================

  /**
   * Clears all tracked timeouts (reel stops, autoplay delays, etc.)
   */
  const clearAllTimeouts = useCallback(() => {
    const timeouts = timeoutsRef.current;
    if (timeouts.length > 0) {
      for (let i = 0; i < timeouts.length; i++) {
        const t = timeouts[i];
        // Support both regular timeout IDs and TickerTimeout instances
        if (t && typeof t.clear === "function") {
          t.clear();
        } else {
          clearTimeout(t);
        }
      }
      timeoutsRef.current = [];
    }
    // The turbo wait window. Left armed it would start the reels turning after
    // the round it belonged to had already ended — see turboSpinningRef.
    if (turboSpinTimerRef.current) {
      turboSpinTimerRef.current.clear();
      turboSpinTimerRef.current = null;
    }
    turboSpinningRef.current = false;
    // Also clear reel-specific TickerTimeout entries
    for (const t of reelStopTimeoutsRef.current.values()) {
      if (t && typeof t.clear === "function") {
        t.clear();
      }
    }
    reelStopTimeoutsRef.current.clear();
  }, []);

  /**
   * Clears connection timeout for regular spins (preserves manual stop timeout)
   */
  const clearConnectionTimeouts = useCallback(() => {
    if (connectionTimeouts.current.main) {
      clearTimeout(connectionTimeouts.current.main);
      // Remove from tracking array
      const idx = timeoutsRef.current.indexOf(connectionTimeouts.current.main);
      if (idx > -1) {
        timeoutsRef.current.splice(idx, 1);
      }
      connectionTimeouts.current.main = null;
    }
  }, []);

  /**
   * Clears connection timeout for manual stop operations
   */
  const clearManualStopTimeouts = useCallback(() => {
    if (connectionTimeouts.current.manualStop) {
      clearTimeout(connectionTimeouts.current.manualStop);
      // Remove from tracking array
      const idx = timeoutsRef.current.indexOf(
        connectionTimeouts.current.manualStop,
      );
      if (idx > -1) {
        timeoutsRef.current.splice(idx, 1);
      }
      connectionTimeouts.current.manualStop = null;
    }
  }, []);

  /**
   * Shows connection popup when server response is delayed
   */
  const showConnectionPopup = useCallback(() => {
    const currentState = store.getState();
    if (!currentState.userInterface.connectionPopupActive) {
      dispatch(openConnectionPopup());
    }
  }, [dispatch]);

  // ============================================================================
  // REDUX SELECTORS
  // ============================================================================

  const autoSpinCount = useSelector(selectAutoSpinCount);
  const autoPlayActive = useSelector(selectAutoPlayActive);
  const remainingAutoSpins = useSelector(selectRemainingAutoSpins);
  const pendingAutoPlaySpeed = useSelector(selectPendingAutoPlaySpeed);
  const autoPlaySpeed = useSelector(selectAutoPlaySpeed);
  const isSpinning = useSelector(selectIsSpinning);
  const manualStop = useSelector(selectManualStop);
  const spinComplete = useSelector(selectSpinComplete);
  const counterAnimationFinished = useSelector(selectCounterAnimationFinished);
  const counterAnimationStarted = useSelector(selectCounterAnimationStarted);
  const winAnimationsPlaying = useSelector(selectWinAnimationsPlaying);
  const replaySessionActive = useSelector(selectReplaySessionActive);
  const replayFinished = useSelector(selectReplayFinished);
  const currentRound = useSelector(selectCurrentRound);
  const balance = useSelector(selectBalance);
  const bet = useSelector(selectBet);
  const menuActive = useSelector(selectMenuActive);
  const stopOnAnyWin = useSelector(selectStopOnAnyWin);
  const stopOnBonusGame = useSelector(selectStopOnBonusGame);
  const stopOnSingleWinExceeds = useSelector(selectStopOnSingleWinExceeds);
  const stopOnBalanceIncreases = useSelector(selectStopOnBalanceIncreases);
  const stopOnBalanceDecreases = useSelector(selectStopOnBalanceDecreases);
  const singleWinExceedsAmount = useSelector(selectSingleWinExceedsAmount);
  const balanceIncreasesAmount = useSelector(selectBalanceIncreasesAmount);
  const balanceDecreasesAmount = useSelector(selectBalanceDecreasesAmount);
  const anyFeatureTriggeredOrActive = useSelector(selectAnyFeatureTriggeredOrActive);
  const holdAndWinActive = useSelector(selectHoldAndWinActive); // Used by tension effects
  const potCollectionTriggered = useSelector(selectPotCollectionTriggered); // Used by turbo eject
  // Bonus lifecycle — read by the replay Ended-modal effect below, which must
  // not fire until a replayed bonus round has fully returned to base game.
  const potCollectionActive = useSelector(selectPotCollectionActive);
  const criticalAnimationPlaying = useSelector(selectCriticalAnimationPlaying);
  const potCollectionOutroTransitionActive = useSelector(
    selectPotCollectionOutroTransitionActive,
  );
  // Free-spins lifecycle — same purpose as the Pot Collection trio above:
  // subscribed only so the replay Ended-modal effect re-runs as each clears.
  const freeSpinsRoomActive = useSelector(selectFreeSpinsActive);
  const freeSpinsRoomTriggered = useSelector(selectFreeSpinsTriggered);
  const showFreeSpinTotalWin = useSelector(selectShowFreeSpinTotalWin);
  // Read for the ended-modal gate below — see freeSpinsTriggerHolding. Both
  // are what decide whether a finished replay is still showing its trigger.
  const showGambleDialog = useSelector(selectShowGambleDialog);
  const showFreeSpinIntro = useSelector(selectShowFreeSpinIntro);
  const showFreeSpinMaxWin = useSelector(selectShowFreeSpinMaxWin);
  const showFreeSpinZeroWin = useSelector(selectShowFreeSpinZeroWin);
  // The free-spins outro wipe. Subscribed for the same reason as the four
  // above — see isReplayRoundSettled.
  const showTransitionDialog = useSelector(selectShowTransitionDialog);


  const errorPopupActive = useSelector(selectErrorPopupActive);
  const infoPopupActive = useSelector(selectInfoPopupActive);
  const freeRoundsCompletionPending = useSelector(
    selectFreeRoundsCompletionPending,
  );
  const gameStatus = useSelector(selectGameStatus);
  const errorCode = useSelector(selectErrorCode);

  // ── Free Rounds Completion Hook ──
  // Orchestrates popups after last free round. Fire-and-forget.
  useFreeRoundsCompletion();

  // ============================================================================
  // ERROR HANDLING - Stop autoplay on errors (except 205)
  // ============================================================================

  useEffect(() => {
    // Stop autoplay on any error (including error 205 free rounds)
    // This ensures autoplay exits cleanly when free rounds are triggered
    if (gameStatus === "failed" && errorCode && autoPlayActive) {
      dispatch(stopAutoPlay());
    }
  }, [gameStatus, errorCode, autoPlayActive, dispatch]);

  // ============================================================================
  // REEL CONTROL FUNCTIONS
  // ============================================================================

  /**
   * Reset all responsible gambling settings to clean state
   * Called when autoplay stops due to a responsible gambling condition being met
   * Must clear both checkboxes AND values since onChange handlers aren't called when dispatching directly
   */
  const resetResponsibleGamblingSettings = useCallback(() => {
    if (!FEATURES_CONFIG.RESPONSIBLE_GAMBLING.ENABLED) return;

    // Uncheck all checkboxes
    dispatch(setStopOnAnyWin(false));
    dispatch(setStopOnBonusGame(false));
    dispatch(setStopOnSingleWinExceeds(false));
    dispatch(setStopOnBalanceIncreases(false));
    dispatch(setStopOnBalanceDecreases(false));
    // Clear all input values
    dispatch(setSingleWinExceedsAmount(""));
    dispatch(setBalanceIncreasesAmount(""));
    dispatch(setBalanceDecreasesAmount(""));
  }, [dispatch]);

  /**
   * Stops all reels with timing based on spin mode and tension state.
   *
   * Four stop modes:
   *
   * 1. Turbo Mode:
   *    - All reels stop simultaneously (no delays)
   *    - Marks all reels as stopped immediately
   *    - Used when currentSpinMethodRef.current === "turbo"
   *
   * 2. Simultaneous Stop Mode (Manual Stop → Turbo):
   *    - All remaining reels stop simultaneously (no delays)
   *    - Used when switching to turbo mid-spin via manual stop
   *    - Provides instant stop feel for turbo activation
   *
   * 3. Normal Mode:
   *    - Sequential stops with base delay between each reel
   *    - Delay = delay * reelIndex (first reel stops immediately, last reel stops last)
   *    - Used for regular spins and autoplay
   *
   * 4. Tension Mode (trigger symbol-based):
   *    - Reels spin 3x slower than normal for anticipation
   *    - First N reels stop with normal timing
   *    - Reels within tension range use cumulative tension timing
   *    - Triggers tension animations and sounds
   *    - Cascades tension to next reel when each tension reel stops
   *    - CUTOFF: When 6 coins reached from stopped reels, remaining reels stop normally
   *
   * @param {number} delay - Base delay between reel stops (milliseconds)
   * @param {number|null} tensionStartReel - Index where tension mode should start (null = no tension)
   * @param {boolean} simultaneousStop - If true, all reels stop at once (no cascading delay)
   *
   * Clears all timeouts before starting to prevent conflicts.
   * Tracks all timeouts for proper cleanup.
   */
  const stopReels = useCallback(
    (delay, tensionStartReel = null, simultaneousStop = false) => {
      // Only clear reel stop timeouts, NOT reel start timeouts
      // This prevents clearing the reel start timeouts when API responds quickly
      reelStopTimeoutsRef.current.clear();

      // Check for per-mode reel stop delay override
      const currentState = store.getState();
      const currentSpeed = currentState.settings.autoPlaySpeed;
      const speedConfig =
        FEATURES_CONFIG.SPIN.AUTO_PLAY_SPEEDS[currentSpeed?.toUpperCase()];
      const effectiveDelay =
        speedConfig?.REEL_STOP_DELAY !== undefined
          ? speedConfig.REEL_STOP_DELAY
          : delay;

      const isTensionMode = tensionStartReel !== null;
      const reels = reelRefs.current;

      // ── Per-reel scatter anticipation (GDD §6.1) ──
      //
      // The board is already known: the server sent the whole outcome before
      // a single reel stopped, so anticipationChain() names the chain reels
      // up front. The ANIMATION then drives the chain, one reel at a time:
      //
      //   reel before the chain lands  -> chain reel 1: in -> loop (x LOOPS)
      //   ...its in + loops elapse     -> chain reel 1 stops
      //   chain reel 1 lands           -> out, in full
      //   out finishes                 -> chain reel 2: in -> loop ...
      //   last chain reel's out ends   -> any reels after the chain stop
      //
      // Every reel after the chain's first trigger reel is held by the chain
      // (not pre-scheduled below) — they keep spinning until their turn.
      // Timing runs on TickerTimeouts from the clips' own durations, never on
      // spine listeners, so a missing spine can slow the chain but never leave
      // a reel spinning.
      //
      // At EVERY speed, turbo and super turbo included: turbo drops the other
      // reels at once and catches the chain reels, one by one — the Zeus
      // tension principle. Vladimir, 2026-09-28: "its not skipped anywhere".
      const round = currentState?.game?.currentRound;
      // flow.round_id; the board array's identity if a round has none (immer
      // keeps it across unrelated updates, and replaces it every spin).
      const roundKey = round?.id ?? round?.reels ?? null;
      // Scatters (GDD §6.1) and coins (GDD §6.2) — one chain either way. The
      // chain itself lives in Games/Shared/anticipationRunner, shared with
      // free spins; this room only holds its reels and forwards the peaks.
      const anticipationRunner = makeAnticipationRunner(effectiveDelay);
      anticipationRunner.sync(round, roundKey);

      // Helper: schedule a tension reel's stop after one full tension cycle.
      // Called reactively when each reel's tension actually STARTS — not pre-scheduled.
      // This ensures every reel gets exactly tensionCycleDuration of tension time,
      // regardless of previous reels' landing bounce durations.
      const scheduleTensionStop = (reelIndex) => {
        const cycleDuration =
          tensionReelRefs.current[reelIndex]?.getTensionCycleDuration?.() ||
          FEATURES_CONFIG.TENSION.DURATION;
        const stopTimeoutId = new TickerTimeout(() => {
          const tidx = timeoutsRef.current.indexOf(stopTimeoutId);
          if (tidx > -1) timeoutsRef.current.splice(tidx, 1);
          reelStopTimeoutsRef.current.delete(reelIndex);

          const tensionWasCanceled = !pendingTensionRef.current;
          if (!tensionWasCanceled) {
            reelRefs.current[reelIndex]?.stopTensionSpin();
            tensionReelRefs.current[reelIndex]?.playTensionOut();
            activeTensionAnimationsRef.current.add(reelIndex);
            tensionAnimationStartTimesRef.current.set(reelIndex, Date.now());
          } else {
            reelRefs.current[reelIndex]?.stopSpin();
          }
          stoppedReelsRef.current.add(reelIndex);
        }, cycleDuration);
        timeoutsRef.current.push(stopTimeoutId);
        reelStopTimeoutsRef.current.set(reelIndex, stopTimeoutId);
      };

      // Unified stop path: speed config controls timing for all modes
      // Turbo without tension: REEL_STOP_DELAYS=[0,0,0,0,0] → all stop at once
      // Turbo with tension: non-tension reels stop at delay 0, tension reels get tension timing
      // Normal/Quick: sequential stops with configured delays
      // ── Anticipation mode: immediately start tension on reel 0 ──────────
      // For scatter/coin tension, tension starts when the last trigger symbol reel lands.
      // But for anticipation (all 5 reels), lastTriggerSymbolReel is -1 — no trigger reel
      // exists, so the initial trigger in onReelLanded never fires for reel 0.
      // Start it immediately here so reel 0 gets the tension animation.
      if (isTensionMode && tensionStartReel === 0 && pendingTensionRef.current?.isAnticipation) {
        // Find the first reel that actually needs tension start.
        // On fresh start this is reel 0. On tab-resume re-entry, earlier
        // reels may have already stopped — start the cascade from the
        // first unstopped reel so the chain doesn't break.
        let effectiveStartReel = 0;
        for (let i = 0; i < reels.length; i++) {
          const ctrl = reels[i]?.getReelController?.();
          if (ctrl && !ctrl.hasStopped) {
            effectiveStartReel = i;
            break;
          }
        }



        // Anticipation: use global DimReelSet overlay (not per-reel dim).
        // This darkens the entire reelset uniformly — Zeus coins will appear bright
        // on the overlay layer above when they land.
        // Only dim on fresh start; on re-entry the overlay is already active.
        if (effectiveStartReel === 0) {
          dimReelSetRef.current?.dimReels();
        }

        soundManager.stop(SOUND_NAME.reel_spin);


        // Start tension animation on effective start reel (skip if already visible from before pause)
        const isTensionAlreadyVisible =
          tensionReelRefs.current[effectiveStartReel]?.isTensionVisible();
        if (!isTensionAlreadyVisible) {
          tensionReelRefs.current[effectiveStartReel]?.playTensionIn();
        }

        // Start tension zoom effect
        if (!holdAndWinActive && !tensionEffectActiveRef.current) {
          const tensionCycleDuration =
            tensionReelRefs.current[effectiveStartReel]?.getTensionCycleDuration?.() ||
            FEATURES_CONFIG.TENSION.DURATION;
          const remainingReels = reels.length - effectiveStartReel;
          const zoomDuration = remainingReels * tensionCycleDuration;
          gameContainerRef?.current?.playTensionEffect?.(zoomDuration);
          tensionEffectActiveRef.current = true;
        }

        // Start tension spin on effective start reel
        const effCtrl = reels[effectiveStartReel]?.getReelController?.();
        if (effCtrl && !effCtrl.isStopping && !effCtrl.hasStopped) {
          // Only start tension spin if not already in tension mode (preserves deceleration progress)
          if (!effCtrl.isTensionMode) {
            reels[effectiveStartReel]?.startTensionSpin();
          }
          scheduleTensionStop(effectiveStartReel);

          // Undim this reel if it was dimmed (it's the active tension reel)
          if (dimmedReelsRef.current.has(effectiveStartReel)) {
            reels[effectiveStartReel]?.undimReel();
            dimmedReelsRef.current.delete(effectiveStartReel);
          }
        }
      }

      // Sequential stops with calculated delays
      for (let index = 0; index < reels.length; index++) {
          const reel = reels[index];

          // Skip reels that have already stopped (prevents rescheduling on tab return)
          // BUT: Don't skip if tension is currently visible (reel is in tension mode)
          const reelController = reel?.getReelController?.();
          const isTensionVisible =
            tensionReelRefs.current[index]?.isTensionVisible();

          if (
            reelController &&
            reelController.hasStopped &&
            !isTensionVisible
          ) {
            continue;
          }

          // If reel is stopping but tension is visible, let it continue (it's in tension mode)
          if (
            reelController &&
            reelController.isStopping &&
            !isTensionVisible
          ) {
            continue;
          }

          // Tension reels: stops are scheduled reactively via scheduleTensionStop()
          // when their tension actually starts. No pre-scheduling needed here.
          // onReelLanded callback (registered below) handles the cascade chain.
          if (isTensionMode && index >= tensionStartReel && !simultaneousStop) {
            // Skip — this reel's stop will be scheduled by scheduleTensionStop()
          } else if (anticipationRunner.holds(index)) {
            // Skip — the anticipation chain stops this reel on its turn.
          } else {
            let finalDelay;
            if (simultaneousStop) {
              finalDelay = 0;
            } else {
              // Normal mode: Non-linear cascading stops (gaps decrease for later reels)
              // Per-speed-mode delays take priority, then manual/auto global delays
              const isManualStop =
                effectiveDelay <= FEATURES_CONFIG.SPIN.STOP_DELAY_MANUAL;
              const reelStopDelays =
                speedConfig?.REEL_STOP_DELAYS ||
                (isManualStop
                  ? FEATURES_CONFIG.SPIN.REEL_STOP_DELAYS_MANUAL
                  : FEATURES_CONFIG.SPIN.REEL_STOP_DELAYS_AUTO);
              finalDelay = reelStopDelays
                ? reelStopDelays[index] || effectiveDelay * index
                : effectiveDelay * index;
            }

            const tickerTimeout = new TickerTimeout(() => {
              const idx = timeoutsRef.current.indexOf(tickerTimeout);
              if (idx > -1) {
                timeoutsRef.current.splice(idx, 1);
              }
              reelStopTimeoutsRef.current.delete(index);

              // Mark the last non-tension reel so it plays the consolidated stop sound
              if (isTensionMode && index === tensionStartReel - 1) {
                const reelCtrl = reel?.getReelController?.();
                if (reelCtrl) {
                  reelCtrl.isLastNonTensionReel = true;
                }
              }
              reel?.stopSpin();
              stoppedReelsRef.current.add(index);
            }, finalDelay);
            timeoutsRef.current.push(tickerTimeout);
            reelStopTimeoutsRef.current.set(index, tickerTimeout);
          }

          // --- Register onReelLanded callback for tension triggering ---
          // This fires when the reel's bounce animation completes (after landing animations start),
          // giving perfect visual sync between the trigger symbol appearing and tension starting.
          reel?.setOnReelLanded?.((landedReelIndex) => {
            const tensionInfo = pendingTensionRef.current;
            if (!tensionInfo) return;

            // --- CUTOFF CHECKS ONLY (remain in onReelLanded for accurate final state) ---
            const tensionWasCanceled = !pendingTensionRef.current;
            if (
              isTensionMode &&
              landedReelIndex >= tensionStartReel &&
              !tensionWasCanceled
            ) {
              // Check cutoff threshold
              const tensionInfoCurrent = pendingTensionRef.current;
              if (tensionInfoCurrent && tensionInfoCurrent.cutoffThreshold) {
                // Use pre-computed scatter positions from RGS adapter
                const currentState = store.getState();
                const currentRound = currentState?.game?.currentRound;
                const scatterPositions = currentRound?.scatterPositions || [];

                // Count total scatters on already-landed reels (not unique reels)
                // e.g. reel 0 has 1 scatter + reel 1 has 2 scatters = 3 total → hits cutoff
                const currentCoinCount = scatterPositions
                  .filter(([reelIndex]) => reelIndex <= landedReelIndex)
                  .length;

                // If cutoff threshold reached, cancel tension for remaining reels
                // Stop them with manual-stop-speed delays (fast cascade, no lingering)
                const nextReelIndex = landedReelIndex + 1;

                if (
                  currentCoinCount >= tensionInfoCurrent.cutoffThreshold &&
                  nextReelIndex < reelRefs.current.length
                ) {

                  // Use manual stop delays for fast cascading stops
                  const manualDelays =
                    FEATURES_CONFIG.SPIN.REEL_STOP_DELAYS_MANUAL;
                  const manualDelay = FEATURES_CONFIG.SPIN.STOP_DELAY_MANUAL;

                  for (
                    let i = nextReelIndex;
                    i < reelRefs.current.length;
                    i++
                  ) {
                    const existingTimeout = reelStopTimeoutsRef.current.get(i);
                    if (existingTimeout) {
                      if (existingTimeout && typeof existingTimeout.clear === "function") {
                        existingTimeout.clear();
                      }
                      reelStopTimeoutsRef.current.delete(i);
                      const timeoutIdx =
                        timeoutsRef.current.indexOf(existingTimeout);
                      if (timeoutIdx > -1) {
                        timeoutsRef.current.splice(timeoutIdx, 1);
                      }
                    }

                    // Calculate delay relative to cutoff reel (same as manual stop cascade)
                    const reelOffset = i - nextReelIndex;
                    const fastDelay = manualDelays
                      ? manualDelays[reelOffset] || manualDelay * reelOffset
                      : manualDelay * reelOffset;

                    const newTimeoutId = new TickerTimeout(() => {
                      const tidx = timeoutsRef.current.indexOf(newTimeoutId);
                      if (tidx > -1) {
                        timeoutsRef.current.splice(tidx, 1);
                      }
                      reelStopTimeoutsRef.current.delete(i);

                      reelRefs.current[i]?.stopSpin();
                      stoppedReelsRef.current.add(i);
                    }, fastDelay);

                    timeoutsRef.current.push(newTimeoutId);
                    reelStopTimeoutsRef.current.set(i, newTimeoutId);

                    if (dimmedReelsRef.current.has(i)) {
                      reelRefs.current[i]?.undimReel();
                      dimmedReelsRef.current.delete(i);
                    }
                    tensionReelRefs.current[i]?.forceStopTension();
                    tensionReels.current.delete(i);
                  }

                  // Anticipation: undim global overlay when cutoff reached
                  if (tensionInfoCurrent?.isAnticipation) {
                    dimReelSetRef.current?.undimReels();
                  }
                  pendingTensionRef.current = null;
                  return;
                }
              }

              // ── Anticipation Zeus cutoff (GDD §10.3 step 7) ────────────────────
              if (tensionInfoCurrent?.isAnticipation && tensionInfoCurrent.zeusReelIndices) {
                const zeusReels = tensionInfoCurrent.zeusReelIndices;
                const hasZeusLanded = zeusReels.size > 0 &&
                  [...zeusReels].some(r => r <= landedReelIndex);
                const hasZeusRemaining = [...zeusReels].some(r => r > landedReelIndex);
                const nextReelIdx = landedReelIndex + 1;

                if (hasZeusLanded && !hasZeusRemaining && nextReelIdx < reelRefs.current.length) {
                  const manualDelays = FEATURES_CONFIG.SPIN.REEL_STOP_DELAYS_MANUAL;
                  const manualDelay = FEATURES_CONFIG.SPIN.STOP_DELAY_MANUAL;

                  for (let i = nextReelIdx; i < reelRefs.current.length; i++) {
                    const existingTimeout = reelStopTimeoutsRef.current.get(i);
                    if (existingTimeout) {
                      if (existingTimeout && typeof existingTimeout.clear === "function") {
                        existingTimeout.clear();
                      }
                      reelStopTimeoutsRef.current.delete(i);
                      const timeoutIdx = timeoutsRef.current.indexOf(existingTimeout);
                      if (timeoutIdx > -1) timeoutsRef.current.splice(timeoutIdx, 1);
                    }

                    const reelOffset = i - nextReelIdx;
                    const fastDelay = manualDelays
                      ? manualDelays[reelOffset] || manualDelay * reelOffset
                      : manualDelay * reelOffset;

                    const newTimeoutId = new TickerTimeout(() => {
                      const tidx = timeoutsRef.current.indexOf(newTimeoutId);
                      if (tidx > -1) timeoutsRef.current.splice(tidx, 1);
                      reelStopTimeoutsRef.current.delete(i);
                      reelRefs.current[i]?.stopSpin();
                      stoppedReelsRef.current.add(i);
                    }, fastDelay);

                    timeoutsRef.current.push(newTimeoutId);
                    reelStopTimeoutsRef.current.set(i, newTimeoutId);

                    tensionReelRefs.current[i]?.forceStopTension();
                    tensionReels.current.delete(i);
                  }

                  dimReelSetRef.current?.undimReels();
                  pendingTensionRef.current = null;
                  return;
                }
              }
            }
          });

          // --- Register onLandingPeak callback for early tension cascade ---
          // Fires at the overshoot trough (lowest visual point, before bounce-back).
          // All tension triggers (initial, edge-case, cascade) fire here for tight timing.
          reel?.setOnLandingPeak?.((peakReelIndex) => {
            // ── Walk the anticipation chain ──
            //
            // On the landing PEAK, not the landed callback, and for the reason
            // tension picked the same hook: the peak is the overshoot trough,
            // the frame the symbol reads as having arrived.
            //
            // The reel that brings the symbol before the trigger starts the
            // chain; each chain reel arriving plays its out and hands on.
            anticipationRunner.onLandingPeak(peakReelIndex);

            const tensionInfo = pendingTensionRef.current;
            if (!tensionInfo) return;

            // --- INITIAL TENSION TRIGGER ---
            // When the last trigger symbol reel peaks, start tension on the first tension reel
            if (
              peakReelIndex === tensionInfo.lastTriggerSymbolReel &&
              peakReelIndex < tensionInfo.tensionStartReel
            ) {
              const firstTensionReel = tensionInfo.tensionStartReel;

              // Dim all reels ahead of the first tension reel
              for (
                let i = firstTensionReel + 1;
                i < reelRefs.current.length;
                i++
              ) {
                if (reelRefs.current[i]?.dimReel) {
                  reelRefs.current[i].dimReel();
                  dimmedReelsRef.current.add(i);
                }
              }

              soundManager.stop(SOUND_NAME.reel_spin);


              // Start tension animation on the first tension reel
              const isAlreadyVisible =
                tensionReelRefs.current[firstTensionReel]?.isTensionVisible();
              const reelCtrl =
                reelRefs.current[firstTensionReel]?.getReelController?.();
              const reelHasStopped =
                reelCtrl?.hasStopped || reelCtrl?.isStopping;

              if (reelHasStopped && isAlreadyVisible) {
                tensionReelRefs.current[firstTensionReel]?.hideTension();
              } else if (!isAlreadyVisible && !reelHasStopped) {
                tensionReelRefs.current[firstTensionReel]?.playTensionIn();
              }

              // Start tension zoom effect (only once per spin, only if HoldAndWin is not active)
              if (!holdAndWinActive && !tensionEffectActiveRef.current) {
                const tensionReelCount =
                  reelRefs.current.length - firstTensionReel;
                const tensionCycleDuration =
                  tensionReelRefs.current[
                    firstTensionReel
                  ]?.getTensionCycleDuration?.() ||
                  FEATURES_CONFIG.TENSION.DURATION;
                const zoomDuration = tensionReelCount * tensionCycleDuration;
                gameContainerRef?.current?.playTensionEffect?.(zoomDuration);
                tensionEffectActiveRef.current = true;
              }

              // Start tension spin on first tension reel
              if (reelRefs.current[firstTensionReel]) {
                if (reelCtrl && !reelCtrl.isStopping && !reelCtrl.hasStopped) {
                  reelRefs.current[firstTensionReel].startTensionSpin();
                  scheduleTensionStop(firstTensionReel);
                  if (dimmedReelsRef.current.has(firstTensionReel)) {
                    reelRefs.current[firstTensionReel]?.undimReel();
                    dimmedReelsRef.current.delete(firstTensionReel);
                  }
                }
              }
            }
            // --- EDGE CASE: trigger reel IS the first tension reel ---
            else if (
              peakReelIndex === tensionInfo.lastTriggerSymbolReel &&
              peakReelIndex >= tensionInfo.tensionStartReel
            ) {
              const firstTensionReel = tensionInfo.tensionStartReel;
              const reelCtrl =
                reelRefs.current[firstTensionReel]?.getReelController?.();
              if (reelCtrl && !reelCtrl.isStopping && !reelCtrl.hasStopped) {
                soundManager.stop(SOUND_NAME.reel_spin);


                const isAlreadyVisible =
                  tensionReelRefs.current[firstTensionReel]?.isTensionVisible();
                const reelHasStopped =
                  reelCtrl?.hasStopped || reelCtrl?.isStopping;

                if (reelHasStopped && isAlreadyVisible) {
                  tensionReelRefs.current[firstTensionReel]?.hideTension();
                } else if (!isAlreadyVisible && !reelHasStopped) {
                  tensionReelRefs.current[firstTensionReel]?.playTensionIn();
                }

                if (!holdAndWinActive && !tensionEffectActiveRef.current) {
                  const tensionReelCount =
                    reelRefs.current.length - firstTensionReel;
                  const tensionCycleDuration =
                    tensionReelRefs.current[
                      firstTensionReel
                    ]?.getTensionCycleDuration?.() ||
                    FEATURES_CONFIG.TENSION.DURATION;
                  const zoomDuration = tensionReelCount * tensionCycleDuration;
                  gameContainerRef?.current?.playTensionEffect?.(zoomDuration);
                  tensionEffectActiveRef.current = true;
                }

                reelRefs.current[firstTensionReel].startTensionSpin();
                scheduleTensionStop(firstTensionReel);
              }
            }

            // --- TENSION CASCADE: chain tension from reel to reel ---
            const tensionWasCanceled = !pendingTensionRef.current;
            if (
              isTensionMode &&
              peakReelIndex >= tensionStartReel &&
              !tensionWasCanceled
            ) {
              const nextReelIndex = peakReelIndex + 1;

              // ── Early anticipation cutoff at peak (GDD §10.3 step 7) ──────
              // When the last zeus reel peaks (symbols become visible at bounce
              // trough), fast-stop all remaining reels immediately. This avoids
              // starting a wasted tension cycle on the next reel after the last
              // zeus orb is already on screen.
              const tensionInfoCascade = pendingTensionRef.current;
              if (
                tensionInfoCascade?.isAnticipation &&
                tensionInfoCascade.zeusReelIndices &&
                nextReelIndex < reelRefs.current.length
              ) {
                const zeusReels = tensionInfoCascade.zeusReelIndices;
                const isLastZeusReel = zeusReels.has(peakReelIndex);
                const hasZeusRemaining = [...zeusReels].some(r => r > peakReelIndex);

                if (isLastZeusReel && !hasZeusRemaining) {
                  // Fast-stop all remaining reels (no more tension needed)
                  const manualDelays = FEATURES_CONFIG.SPIN.REEL_STOP_DELAYS_MANUAL;
                  const manualDelay = FEATURES_CONFIG.SPIN.STOP_DELAY_MANUAL;

                  for (let i = nextReelIndex; i < reelRefs.current.length; i++) {
                    const existingTimeout = reelStopTimeoutsRef.current.get(i);
                    if (existingTimeout) {
                      if (existingTimeout && typeof existingTimeout.clear === "function") {
                        existingTimeout.clear();
                      }
                      reelStopTimeoutsRef.current.delete(i);
                      const timeoutIdx = timeoutsRef.current.indexOf(existingTimeout);
                      if (timeoutIdx > -1) timeoutsRef.current.splice(timeoutIdx, 1);
                    }

                    const reelOffset = i - nextReelIndex;
                    const fastDelay = manualDelays
                      ? manualDelays[reelOffset] || manualDelay * reelOffset
                      : manualDelay * reelOffset;

                    const newTimeoutId = new TickerTimeout(() => {
                      const tidx = timeoutsRef.current.indexOf(newTimeoutId);
                      if (tidx > -1) timeoutsRef.current.splice(tidx, 1);
                      reelStopTimeoutsRef.current.delete(i);
                      reelRefs.current[i]?.stopSpin();
                      stoppedReelsRef.current.add(i);
                    }, fastDelay);

                    timeoutsRef.current.push(newTimeoutId);
                    reelStopTimeoutsRef.current.set(i, newTimeoutId);

                    tensionReelRefs.current[i]?.forceStopTension();
                    tensionReels.current.delete(i);
                  }

                  dimReelSetRef.current?.undimReels();
                  pendingTensionRef.current = null;
                  return;
                }
              }

              if (nextReelIndex < reelRefs.current.length) {
                soundManager.stop(SOUND_NAME.reel_spin);


                const nextReelController =
                  reelRefs.current[nextReelIndex]?.getReelController?.();
                const nextReelHasStopped =
                  nextReelController?.hasStopped ||
                  nextReelController?.isStopping;
                const isAlreadyVisible =
                  tensionReelRefs.current[nextReelIndex]?.isTensionVisible();

                if (nextReelHasStopped && isAlreadyVisible) {
                  tensionReelRefs.current[nextReelIndex]?.hideTension();
                } else if (!isAlreadyVisible && !nextReelHasStopped) {
                  tensionReelRefs.current[nextReelIndex]?.playTensionIn();
                }

                if (reelRefs.current[nextReelIndex]) {
                  const reelCtrl =
                    reelRefs.current[nextReelIndex].getReelController?.();
                  if (
                    reelCtrl &&
                    !reelCtrl.isStopping &&
                    !reelCtrl.hasStopped
                  ) {
                    reelRefs.current[nextReelIndex].startTensionSpin();
                    scheduleTensionStop(nextReelIndex);
                    if (dimmedReelsRef.current.has(nextReelIndex)) {
                      reelRefs.current[nextReelIndex]?.undimReel();
                      dimmedReelsRef.current.delete(nextReelIndex);
                    }
                  }
                }
              } else if (pendingTensionRef.current?.isAnticipation) {
                // Last tension reel peaked during anticipation — undim global overlay
                dimReelSetRef.current?.undimReels();
                pendingTensionRef.current = null;
              }
            }
          });
        }


      currentSpinMethodRef.current = null;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clearAllTimeouts, dispatch],
  );

  /**
   * Freezes all reels instantly at their current position
   * Used for error popups (e.g., error 205 free rounds) where reels should freeze mid-spin
   * No landing animations, no sound effects, just instant freeze
   */
  const freezeReels = useCallback(() => {
    const reels = reelRefs.current;

    // Freeze all reels instantly
    for (let i = 0; i < reels.length; i++) {
      reels[i]?.freezeSpin();
    }

    // Stop all sounds
    soundManager.stop(SOUND_NAME.reel_spin);


    // Clear all timeouts
    clearAllTimeouts();

    // Reset state flags
    stopTriggeredRef.current = true;
    pendingManualStopRef.current = false;
    pendingTensionRef.current = null;
    tensionReels.current = new Set();
    activeTensionAnimationsRef.current.clear();
    tensionAnimationStartTimesRef.current.clear();
    dimmedReelsRef.current.clear();
    tensionEffectActiveRef.current = false;

    // Undim all reels (per-reel + global DimReelSet safety)
    for (let i = 0; i < reelRefs.current.length; i++) {
      reelRefs.current[i]?.undimReel();
    }
    dimReelSetRef.current?.undimReels();

    // Stop tension effect animation
    gameContainerRef?.current?.stopTensionEffect?.();

    // Mark reels as frozen (need recovery)
    dispatch(setReelsFrozen(true));
  }, [reelRefs, clearAllTimeouts, gameContainerRef, dimReelSetRef, dispatch]);

  /**
   * Restores all reels to idle state with specific symbols
   * Used after error recovery to show previous round's symbols properly
   * Called during error recovery transition overlay
   * @param {Array} reelsData - Array of symbol IDs for each reel
   * @param {number|null} betValue - Optional bet value to use for coin texts (for error recovery)
   */
  const restoreReels = useCallback(
    (reelsData, betValue = null) => {
      if (!reelsData || !Array.isArray(reelsData)) {
        return;
      }

      const reels = reelRefs.current;

      // Restore each reel to idle state with its symbols
      for (let i = 0; i < reels.length && i < reelsData.length; i++) {
        const symbolIds = reelsData[i];
        if (symbolIds && Array.isArray(symbolIds)) {
          reels[i]?.restoreToIdleState(symbolIds, betValue);
        }
      }

      // Reset all state flags
      stopTriggeredRef.current = false;
      pendingManualStopRef.current = false;
      pendingTensionRef.current = null;
      tensionReels.current = new Set();
      activeTensionAnimationsRef.current.clear();
      tensionAnimationStartTimesRef.current.clear();
      dimmedReelsRef.current.clear();
      tensionEffectActiveRef.current = false;
    },
    [reelRefs],
  );

  /**
   * Replay exit — put the physical board back (docs/replay-history-spec.md §0).
   *
   * endReplaySession restores the live round in Redux (gameSettingsSlice's
   * snapshot), but Redux doesn't move reel sprites: they were physically spun
   * to the replayed outcome and would otherwise stay there, showing a round
   * the player never played, possibly still lit up with its win lines.
   *
   * Reuses the same restoreReels() error recovery uses for exactly this —
   * snapping every reel to a known screen without spinning — plus the win
   * teardown handleSpin does at the start of a spin.
   *
   * Fires only on the true exit (session active → inactive), which is the
   * History board's own close. "Replay Again" keeps the session active, and a
   * standalone replay page never ends its session at all, so neither triggers
   * this.
   */
  const prevReplaySessionActiveRef = useRef(replaySessionActive);
  useEffect(() => {
    const wasActive = prevReplaySessionActiveRef.current;
    prevReplaySessionActiveRef.current = replaySessionActive;
    if (!wasActive || replaySessionActive) return;

    // Clear the replayed round's win visuals (lines, boxes, dim overlay).
    for (let i = 0; i < reelRefs.current.length; i++) {
      reelRefs.current[i]?.undimReel();
    }
    if (snapClearAllRef.current) {
      snapClearAllRef.current();
    } else if (dimReelSetRef.current?.snapClear) {
      dimReelSetRef.current.snapClear();
    }

    // Snap the reels back to the live round restored by endReplaySession.
    const liveState = store.getState();
    const liveRound = liveState.game?.currentRound;
    if (liveRound?.reels) {
      restoreReels(liveRound.reels, liveState.game?.bet);
    }

    // Drop the replayed bonus's Total Win. WinDisplay's isPCMode reads
    // potCollection.currentRound.win + the outro-counter flag, and outranks the
    // restored live round's win — so without this a replayed bonus keeps
    // showing its total over the live game until the next spin.
    dispatch(clearPotCollectionWin());
    dispatch(setPotCollectionOutroCounterFinished(false));

    // Clear useWinController's dedupe + "already animated" latches, both left
    // set by the replayed round. Required either way: without it no future win
    // animates, and the replay of the live round below can't run.
    dispatch(setResetMainGameVisualEffects(true));
    dispatch(setCounterAnimationStarted(false));
    dispatch(setCounterAnimationFinished(false));

    // Restart symbol idles — restoreToIdleState above sets the textures but
    // not their looping animations, so without this the board comes back
    // frozen. Deferred a frame for the same reason GameView and
    // ErrorRecoveryOverlay defer theirs: the sprites must have committed first.
    requestAnimationFrame(() => triggerInitialIdleAnimations(false));

    // Replay the live round's own win, so the return is exact rather than a
    // winning board with nothing marking it. Purely presentational — the win
    // was credited before the player ever opened History and replay never
    // touched the live balance, so nothing is paid twice.
    //
    // Same nudge ErrorRecoveryOverlay uses to re-drive the win controller:
    // its main effect gates on spinComplete, so toggling it re-fires with the
    // latches above cleared.
    //
    // Gated on an actual win amount, deliberately not on triggerWin — a
    // trigger round must never re-enter its feature from here.
    if ((liveRound?.win || 0) <= 0) return;

    // Never re-drive the win controller while a live free-rounds campaign is
    // running. Re-showing the win makes WinDisplay's FR effects fire a SECOND
    // time for a round they already counted: displayedWin gets incremented
    // again, and on the campaign's last round StorageManager.updateFreeRoundsWin
    // persists that inflated total to the player's real storage. The win text
    // is worth far less than corrupting a live campaign, so it's skipped —
    // the reels and every other piece of state are still restored above.
    if (liveState.freeRounds?.status === "active") return;

    dispatch(setSpinComplete(false));
    let nudgeFired = false;
    const replayWinNudge = new TickerTimeout(() => {
      nudgeFired = true;
      dispatch(setSpinComplete(true));
    }, 50);
    return () => {
      replayWinNudge.clear();
      // Cancelling the timer must never leave spinComplete false — that flag
      // gates the win controller AND the spin button, so a teardown inside
      // this 50ms window would soft-lock the game with no way back.
      if (!nudgeFired) dispatch(setSpinComplete(true));
    };
  }, [
    replaySessionActive,
    restoreReels,
    reelRefs,
    snapClearAllRef,
    dimReelSetRef,
    triggerInitialIdleAnimations,
    dispatch,
  ]);

  /**
   * Emergency Tension State Cleanup Effect
   * Prevents memory leaks from orphaned tension state if a spin completes unexpectedly.
   * Runs every 10 seconds to detect and clean up any tension state that wasn't properly cleared.
   * Only cleans up when no spin is active to avoid interfering with active gameplay.
   */
  useEffect(() => {
    const emergencyCleanup = () => {
      if (pendingTensionRef.current && !isSpinning) {
        pendingTensionRef.current = null;
        tensionReels.current = new Set();
        stoppedReelsRef.current.clear();
        lastForceStoppedReelRef.current = -1;
        activeTensionAnimationsRef.current.clear();
        tensionAnimationStartTimesRef.current.clear();
        // Don't stop shake animation here - let it play until spin completes
      }
    };

    const cleanupInterval = setInterval(emergencyCleanup, 10000);

    return () => {
      clearInterval(cleanupInterval);
      emergencyCleanup();
    };
  }, [isSpinning, gameContainerRef]);

  /**
   * API Response Detection & Reel Stopping Effect
   *
   * Primary authority for stopping reels based on API responses. Subscribes to Redux store
   * changes when a spin is active and handles two scenarios:
   *
   * 1. Regular Spin Flow:
   *    - Sets connection timeout based on expected spin duration (turbo/quick/auto)
   *    - Waits for successful API response with reel data
   *    - Detects trigger symbols (coin/scatter based on config) for tension mode activation
   *    - Applies speed-specific animation optimizations (suppresses landing animations in turbo)
   *    - Calculates minimum spin duration and schedules reel stops accordingly
   *    - Manages connection timeout popups for slow server responses
   *
   * 2. Manual Stop Flow:
   *    - Subscribes separately when manual stop is triggered
   *    - Waits for API response to arrive
   *    - Applies per-reel landing animation suppression based on win presence
   *    - Stops reels with manual stop timing (faster than auto)
   *
   * Handles fake spin mode (insufficient credits) and cleans up subscriptions properly.
   */
  useEffect(() => {
    let intervalId;
    let unsubscribe;
    let unsubManualCleanup;
    let triggered = false;

    const shouldSubscribe = isSpinning && !manualStop && !spinComplete;


    if (shouldSubscribe) {
      if (isSubscribedRef.current) {
        return;
      }
      isSubscribedRef.current = true;

      // Set connection timeout based on expected spin duration
      const currentState = store.getState();
      const currentAutoPlaySpeed = currentState.settings.autoPlaySpeed;
      let expectedDuration;

      if (isTurboLike(currentAutoPlaySpeed)) {
        // ── NOT turbo's 5ms minimum ──
        //
        // This is "how long before a spin is abnormally slow", not "how long a
        // spin takes". While turbo WAITED for the server, 5ms + 350 was almost
        // right — the board was frozen, so anything over a third of a second
        // was worth explaining. Now turbo SPINS while it waits, exactly as auto
        // does, and a spinning reel is its own feedback. Left at 355ms the
        // popup would interrupt every slightly-slow turbo spin.
        //
        // AUTO's threshold is the honest one for a spin-and-wait mode, and it
        // keeps all speeds saying "slow" at the same moment.
        expectedDuration =
          FEATURES_CONFIG.SPIN.AUTO_PLAY_SPEEDS.AUTO.SPIN_DURATION;
      } else if (currentAutoPlaySpeed === "quick") {
        expectedDuration =
          FEATURES_CONFIG.SPIN.AUTO_PLAY_SPEEDS.QUICK.SPIN_DURATION;
      } else {
        expectedDuration =
          FEATURES_CONFIG.SPIN.AUTO_PLAY_SPEEDS.AUTO.SPIN_DURATION;
      }

      const naturalSpinDelay = CONNECTION_POPUP_TIMEOUTS.NATURAL_SPIN_DELAY;
      connectionTimeouts.current.main = setTimeout(() => {
        if (!triggered) {
          showConnectionPopup();
        }
      }, expectedDuration + naturalSpinDelay);
      // Also track in main timeouts array for proper cleanup
      timeoutsRef.current.push(connectionTimeouts.current.main);

      const onStoreChange = () => {
        if (triggered) return;

        const state = store.getState();
        const currentRound = state.game.currentRound;
        const currentStatus = state.spin.status;

        // Check for ANY error FIRST (before checking for reels)
        // All errors now freeze reels instead of landing on fake patterns
        // Catches both structured errors (with errorCode) and generic network failures
        if (currentStatus === "failed") {
          triggered = true;
          clearConnectionTimeouts();

          const uiState = store.getState();
          if (uiState.userInterface.connectionPopupActive) {
            dispatch(closeConnectionPopup());
          }

          // Freeze all reels instantly (no landing animation)
          requestAnimationFrame(() => {
            freezeReels();
          });

          if (unsubscribe) unsubscribe();
          return;
        }

        // Handle successful spins - proceed to stop reels normally
        if (currentStatus === "succeeded" && currentRound?.reels?.length) {
          // API responded successfully, proceed to stop reels normally
        } else {
          return; // Wait for API response with reel data
        }

        triggered = true;
        clearConnectionTimeouts();

        const uiState = store.getState();
        if (uiState.userInterface.connectionPopupActive) {
          dispatch(closeConnectionPopup());
        }

        const currentAutoPlaySpeed = state.settings.autoPlaySpeed;
        const reels = currentRound.reels;

        // Handle fake spin mode
        if (state.spin.fakeSpinMode) {
          requestAnimationFrame(() => {
            stopReels(FEATURES_CONFIG.SPIN.STOP_DELAY_AUTO, null);
          });
          if (unsubscribe) unsubscribe();
          return;
        }

        // Check for wins and apply speed-specific optimizations
        const hasStructuredWins =
          currentRound?.wins && Object.keys(currentRound.wins).length > 0;
        const hasAmountWin = !!currentRound?.win && currentRound.win > 0;
        const hasWin = hasStructuredWins || hasAmountWin;

        // Check for Hold & Win trigger (6+ coins) - even if no win amount
        const isHoldAndWinTrigger = detectHoldAndWinTrigger(currentRound);

        // Speed-specific animation optimizations
        if (isTurboLike(currentAutoPlaySpeed)) {
          // Turbo: per-reel landing suppression — only for non-tension reels
          // Tension reels need landing animations for the cascade visual
          if (hasWin || isHoldAndWinTrigger) {
            suppressLandingAnimationsRef.current = true;
          }
          // NOTE: If tension is detected below, we'll narrow this to non-tension reels only
        } else if (
          currentAutoPlaySpeed === "auto" ||
          currentAutoPlaySpeed === "quick"
        ) {
          if (hasWin) {
            suppressLandingAnimationsPerReelRef.current.add(4);
          } else {
            suppressLandingAnimationsPerReelRef.current.delete(4);
          }
        } else {
          suppressLandingAnimationsPerReelRef.current.delete(3);
          suppressLandingAnimationsPerReelRef.current.delete(4);
        }

        // Detect trigger symbols (coin/scatter) for tension mode
        // Uses pre-computed scatterCount and scatterPositions from RGS adapter
        let shouldApplyTension = null;

        if (FEATURES_CONFIG.TENSION.SYMBOL_COUNTING_ENABLED && !isSuperTurbo(currentAutoPlaySpeed)) {
          const minSymbols = FEATURES_CONFIG.TENSION.MIN_SYMBOLS;
          const cutoffThreshold = FEATURES_CONFIG.TENSION.CUTOFF_THRESHOLD;

          // Read pre-computed trigger data from currentRound (set by RGS adapter)
          // Coins: coinCount/coinPositions for hold & win / royal collect
          // Scatters: scatterCount/scatterPositions for free spins
          // When both exist, use whichever has the higher count for tension activation
          const cc = currentRound.coinCount || 0;
          const sc = currentRound.scatterCount || 0;
          const coinCount = Math.max(cc, sc);
          const triggerPositions = cc >= sc
            ? (currentRound.coinPositions || [])
            : (currentRound.scatterPositions || []);


          // Build per-reel trigger counts (handles multiple coins/scatters per reel)
          const triggerCountByReel = new Map();
          for (const [r] of triggerPositions) {
            triggerCountByReel.set(r, (triggerCountByReel.get(r) || 0) + 1);
          }
          // Get sorted unique reel indices with trigger symbols
          const sortedTriggerReels = [...triggerCountByReel.keys()].sort((a, b) => a - b);

          if (coinCount >= minSymbols) {
            // Walk reels left-to-right, accumulating scatter count
            // When cumulative count reaches minSymbols, tension starts on the NEXT reel
            let cumulativeCount = 0;
            let tensionStartReel = -1;
            let lastTriggerSymbolReel = -1;

            for (let i = 0; i < sortedTriggerReels.length; i++) {
              const reelIdx = sortedTriggerReels[i];
              cumulativeCount += triggerCountByReel.get(reelIdx);
              if (cumulativeCount >= minSymbols && tensionStartReel === -1) {
                lastTriggerSymbolReel = reelIdx;
                tensionStartReel = lastTriggerSymbolReel + 1; // Start tension on reel right after last trigger scatter
              }
            }

            // Apply tension if threshold reached and tension reel is within bounds
            if (tensionStartReel !== -1 && tensionStartReel < reels.length) {
              shouldApplyTension = tensionStartReel;

              // Guard: Only set pendingTensionRef if not already set
              if (!pendingTensionRef.current) {
                pendingTensionRef.current = {
                  tensionStartReel: shouldApplyTension,
                  coinCount: coinCount,
                  lastTriggerSymbolReel: lastTriggerSymbolReel,
                  cutoffThreshold: cutoffThreshold,
                };
                const newTensionReels = new Set();
                for (let i = shouldApplyTension; i < reels.length; i++) {
                  newTensionReels.add(i);
                }
                tensionReels.current = newTensionReels;
              } else {
                // Tension already triggered, just use the existing value
                shouldApplyTension = pendingTensionRef.current.tensionStartReel;
              }
            }
          }
        }

        // ── Anticipation override (GDD §10.2) ──────────────────────────────
        // If RGS flagged this spin as anticipation, force ALL 5 reels into tension
        // (tensionStartReel = 0). Active for ALL speed modes — client requires
        // uniform anticipation visual regardless of turbo/super turbo.
        // This runs AFTER scatter/coin tension so it can override with full-board tension.
        if (
          FEATURES_CONFIG.TENSION.ANTICIPATION?.ENABLED &&
          currentRound.anticipation &&
          shouldApplyTension === null // Don't override existing scatter/coin tension
        ) {
          shouldApplyTension = 0; // All 5 reels enter tension

          if (!pendingTensionRef.current) {
            // Pre-compute which reels contain Zeus symbols (GDD §10.3 step 7)
            // Used during cascade to detect when last Zeus reel has landed
            const zeusReelIndices = new Set();
            const roundReels = currentRound.reels;
            if (roundReels) {
              for (let ri = 0; ri < roundReels.length; ri++) {
                const reelData = roundReels[ri];
                if (reelData) {
                  for (let si = 0; si < reelData.length; si++) {
                    if (BONUS_SYMBOL_IDS.includes(reelData[si])) {
                      zeusReelIndices.add(ri);
                      break;
                    }
                  }
                }
              }
            }

            pendingTensionRef.current = {
              tensionStartReel: 0,
              coinCount: 0,
              lastTriggerSymbolReel: -1, // No specific trigger reel — all reels in tension
              cutoffThreshold: Infinity, // Scatter cutoff disabled — anticipation uses Zeus-based cutoff
              isAnticipation: true, // Flag for downstream consumers
              zeusReelIndices, // Reels containing Zeus symbols (for step 7 cutoff)
            };
            const newTensionReels = new Set();
            for (let i = 0; i < reels.length; i++) {
              newTensionReels.add(i);
            }
            tensionReels.current = newTensionReels;
          }

        }

        // Turbo + tension: narrow landing suppression to non-tension reels only
        // Tension reels need landing animations for the cascade (trigger spine, dimming, etc.)
        if (isTurboLike(currentAutoPlaySpeed) && shouldApplyTension !== null && suppressLandingAnimationsRef.current) {
          suppressLandingAnimationsRef.current = false;
          for (let i = 0; i < shouldApplyTension; i++) {
            suppressLandingAnimationsPerReelRef.current.add(i);
          }
          // Mark ALL reel controllers so scatter symbols get landing→idle even on pre-tension reels
          for (let i = 0; i < reelRefs.current.length; i++) {
            const ctrl = reelRefs.current[i]?.getReelController?.();
            if (ctrl) ctrl.hasTensionThisSpin = true;
          }
        }

        // Turbo + tension: eject spacebar turbo hold so held spacebar doesn't auto-skip tension
        // Only for spacebar-driven turbo — autoplay turbo has no held key to eject
        // Speed stays "turbo" — only the hold is forcefully lifted, NOT the speed setting
        // Cleopatra's Fire: TENSION is off, so shouldApplyTension stays null —
        // the anticipation chain ejects the hold as well.
        if (
          isTurboLike(currentAutoPlaySpeed) &&
          spacebarTurboActive &&
          (shouldApplyTension !== null || !!anticipationChain(currentRound))
        ) {
          setSpacebarTurboActive(false);
          turboPendingActivationRef.current = false;
          // Block held spacebar from auto-skipping tension reels
          turboTensionEjectedRef.current = true;
          // Defer speed restoration to spin complete — prevents speed getting stuck at "turbo"
          turboPendingStopRef.current = true;
        }

        // Centralized stop command — two paths based on speed mode
        const mode = state.settings.autoPlaySpeed;

        // ── Anticipation during turbo/super_turbo: use AUTO timing path ──
        // Client requires uniform anticipation visual. Override turbo's instant
        // "start + stop" pattern with AUTO's "spin for duration → stop with tension".
        // Reel speed already overridden to AUTO above via lockedSpinSpeed.
        const isAnticipationOverride = pendingTensionRef.current?.isAnticipation && isTurboLike(mode);

        // ── TURBO, answer inside the window: the classic DROP ──
        //
        // The reels never started, so start and stop them in the same breath —
        // they travel only the drop distance and fall into place. This is the
        // normal turbo round and it is byte-for-byte the behaviour that existed
        // before any of this.
        if (isTurboLike(mode) && !turboSpinningRef.current) {
          turboSpinTimerRef.current?.clear();
          turboSpinTimerRef.current = null;

          const reels = reelRefs.current;
          for (let i = 0; i < reels.length; i++) {
            reels[i]?.startSpinNoWindUp();
          }
          stopReels(FEATURES_CONFIG.SPIN.STOP_DELAY_AUTO, shouldApplyTension);
          if (unsubscribe) unsubscribe();
          return;
        }

        {
          // ── Everything still turning: min-duration timing ──
          //
          // Auto and quick, and a turbo round whose server missed the window so
          // the reels came alive. What separates them is only the MINIMUM each
          // must spin for, resolved below.

          // Turbo anticipation: the reels ARE already spinning (handleSpin
          // starts every speed now), so this only re-speeds them to AUTO. The
          // startSpinNoWindUp calls below are kept as a harmless no-op —
          // spinNoWindUp returns immediately when isPlaying — so the path is
          // still correct if it is ever reached before the start.
          if (isAnticipationOverride) {
            const reels = reelRefs.current;
            for (let i = 0; i < reels.length; i++) {
              reels[i]?.startSpinNoWindUp();
            }
            // spinNoWindUp() → _startSpinAnimation() caches normalizedSpinSpeed
            // using turbo config. Override lockedSpinSpeed AND recalculate velocity
            // so the ticker actually spins at AUTO speed.
            for (let i = 0; i < reelRefs.current.length; i++) {
              const ctrl = reelRefs.current[i]?.getReelController?.();
              if (ctrl) {
                ctrl.lockedSpinSpeed = "auto";
                ctrl._speedConfigCache = null;
                // Recalculate normalizedSpinSpeed — mirrors _startSpinAnimation() logic
                const speedConfig = ctrl._getSpeedConfig();
                const safeSpeedMs = Math.max(speedConfig.SPEED_MS_PER_SYMBOL_HEIGHT, 17);
                ctrl.normalizedSpinSpeed = (ctrl.symbolHeight / safeSpeedMs) * 1000;
              }
            }
            // Restarted from HERE on purpose. The reel has been spinning at
            // turbo speed since press; the anticipation is meant to run for
            // AUTO's full duration at AUTO's speed from the moment it is
            // recognised, not to inherit time already spent going fast.
            spinStartTimeRef.current = Date.now();
          }

          let minSpinDuration;
          if (isTurboLike(mode) && !isAnticipationOverride) {
            // ── Turbo keeps its OWN minimum, never AUTO's ──
            //
            // Without this branch turbo would fall through to AUTO's 600ms and
            // stop being turbo at all. MIN_SPIN_DURATION is 5ms — always
            // already elapsed when a response arrives — so the reels land
            // immediately on a normal connection and only keep spinning when
            // the server is genuinely slow.
            const turboConfig = isSuperTurbo(mode)
              ? FEATURES_CONFIG.SPIN.AUTO_PLAY_SPEEDS.SUPER_TURBO
              : FEATURES_CONFIG.SPIN.AUTO_PLAY_SPEEDS.TURBO;
            minSpinDuration = turboConfig.MIN_SPIN_DURATION;
          } else if (mode === "quick" && !isAnticipationOverride) {
            minSpinDuration =
              FEATURES_CONFIG.SPIN.AUTO_PLAY_SPEEDS.QUICK.SPIN_DURATION;
          } else {
            // AUTO speed duration — also used for anticipation override
            minSpinDuration =
              FEATURES_CONFIG.SPIN.AUTO_PLAY_SPEEDS.AUTO.SPIN_DURATION;
          }
          const now = Date.now();
          const startTime = spinStartTimeRef.current || 0;
          const elapsed = now - startTime;
          const remaining = minSpinDuration - elapsed;

          if (remaining > 0) {
            const timeoutId = new TickerTimeout(() => {
              // Remove this timeout from tracking since it fired naturally
              const idx = timeoutsRef.current.indexOf(timeoutId);
              if (idx > -1) {
                timeoutsRef.current.splice(idx, 1);
              }
              stopReels(FEATURES_CONFIG.SPIN.STOP_DELAY_AUTO, shouldApplyTension);
            }, remaining);
            timeoutsRef.current.push(timeoutId);
          } else {
            stopReels(FEATURES_CONFIG.SPIN.STOP_DELAY_AUTO, shouldApplyTension);
          }
        }

        if (unsubscribe) unsubscribe();
      };

      unsubscribe = store.subscribe(onStoreChange);
      // Immediate check in case status already succeeded before subscription
      onStoreChange();
    } else if (manualStop && pendingManualStopRef.current) {
      // Prevent duplicate subscriptions if already subscribed
      if (isSubscribedRef.current) {
        return;
      }
      isSubscribedRef.current = true;

      // Manual stop: subscribe once to detect when API response arrives
      const onManualStoreChange = () => {
        const state = store.getState();

        // Handle errors during manual stop — freeze reels and clean up
        if (state.spin.status === "failed") {
          pendingManualStopRef.current = false;
          spinStartTimeRef.current = null;

          // Clear manual stop connection timeouts and hide popup if shown
          clearManualStopTimeouts();
          const uiState = store.getState();
          if (uiState.userInterface.connectionPopupActive) {
            dispatch(closeConnectionPopup());
          }

          // Freeze all reels instantly
          requestAnimationFrame(() => {
            freezeReels();
          });

          if (unsubManualCleanup) unsubManualCleanup();
          return;
        }

        if (state.spin.status !== "succeeded") return;

        pendingManualStopRef.current = false;
        spinStartTimeRef.current = null;

        // Clear manual stop connection timeouts and hide popup if shown
        clearManualStopTimeouts();
        const uiState = store.getState();
        if (uiState.userInterface.connectionPopupActive) {
          dispatch(closeConnectionPopup());
        }

        // Decide per-reel landing suppression for manual stop based on win presence
        try {
          const round = state?.game?.currentRound;
          const hasStructuredWins =
            round?.wins && Object.keys(round.wins).length > 0;
          const hasAmountWin = !!round?.win && round.win > 0;
          const hasWin = hasStructuredWins || hasAmountWin;

          // Check for Hold & Win trigger (6+ coins) - even if no win amount
          const isHoldAndWinTrigger = detectHoldAndWinTrigger(round);

          // Suppress landing animations for wins OR Hold & Win triggers
          if (hasWin || isHoldAndWinTrigger) {
            suppressLandingAnimationsPerReelRef.current = new Set(
              manualStopTargetReelsRef.current,
            );
          } else {
            suppressLandingAnimationsPerReelRef.current.clear();
          }
        } catch (e) {
          suppressLandingAnimationsPerReelRef.current.clear();
        }

        // Clear snapshot to avoid leaking to next spin
        manualStopTargetReelsRef.current.clear();

        // Check if switching to turbo mode - if so, stop all reels simultaneously
        const isSwitchingToTurbo =
          isTurboLike(state.settings.pendingAutoPlaySpeed);

        // Compute tension for manual stop — pre-tension reels stop fast,
        // but tension reels still get the full tension animation
        let manualStopTension = null;
        if (FEATURES_CONFIG.TENSION.SYMBOL_COUNTING_ENABLED && !isSwitchingToTurbo && !isSuperTurbo(state.settings.autoPlaySpeed)) {
          const round = state?.game?.currentRound;
          const minSymbols = FEATURES_CONFIG.TENSION.MIN_SYMBOLS;
          const cutoffThreshold = FEATURES_CONFIG.TENSION.CUTOFF_THRESHOLD;
          // Coins: coinCount/coinPositions for hold & win / royal collect
          // Scatters: scatterCount/scatterPositions for free spins
          const cc = round?.coinCount || 0;
          const sc = round?.scatterCount || 0;
          const coinCount = Math.max(cc, sc);
          const triggerPositions = cc >= sc
            ? (round?.coinPositions || [])
            : (round?.scatterPositions || []);
          // Build per-reel trigger counts (handles multiple coins/scatters per reel)
          const triggerCountByReel = new Map();
          for (const [r] of triggerPositions) {
            triggerCountByReel.set(r, (triggerCountByReel.get(r) || 0) + 1);
          }
          const sortedTriggerReels = [...triggerCountByReel.keys()].sort((a, b) => a - b);

          if (coinCount >= minSymbols) {
            let tensionStartReel = -1;
            let lastTriggerSymbolReel = -1;
            let cumulativeCount = 0;
            for (let i = 0; i < sortedTriggerReels.length; i++) {
              const reelIdx = sortedTriggerReels[i];
              cumulativeCount += triggerCountByReel.get(reelIdx);
              if (cumulativeCount >= minSymbols && tensionStartReel === -1) {
                lastTriggerSymbolReel = reelIdx;
                tensionStartReel = lastTriggerSymbolReel + 1;
              }
            }

            if (
              tensionStartReel !== -1 &&
              tensionStartReel < reelRefs.current.length
            ) {
              manualStopTension = tensionStartReel;

              // Set pendingTensionRef if not already set (needed for tension animations)
              if (!pendingTensionRef.current) {
                pendingTensionRef.current = {
                  tensionStartReel: manualStopTension,
                  coinCount,
                  lastTriggerSymbolReel,
                  cutoffThreshold,
                };
                const newTensionReels = new Set();
                for (
                  let i = manualStopTension;
                  i < reelRefs.current.length;
                  i++
                ) {
                  newTensionReels.add(i);
                }
                tensionReels.current = newTensionReels;
              }
            }
          }
        }

        // ── Anticipation override for manual stop (GDD §10.2) ──
        // Same logic as auto stop: if anticipation flag set, force all 5 reels into tension.
        // Active for ALL speed modes — uniform anticipation visual.
        if (
          FEATURES_CONFIG.TENSION.ANTICIPATION?.ENABLED &&
          !isSwitchingToTurbo &&
          manualStopTension === null
        ) {
          const round = state?.game?.currentRound;
          if (round?.anticipation) {
            manualStopTension = 0;

            if (!pendingTensionRef.current) {
              pendingTensionRef.current = {
                tensionStartReel: 0,
                coinCount: 0,
                lastTriggerSymbolReel: -1,
                cutoffThreshold: Infinity,
                isAnticipation: true,
              };
              const newTensionReels = new Set();
              for (let i = 0; i < reelRefs.current.length; i++) {
                newTensionReels.add(i);
              }
              tensionReels.current = newTensionReels;
            }

            // ── Uniform anticipation speed for manual stop ──
            // Override reel speed to AUTO for turbo/super_turbo so anticipation
            // looks identical across all speed modes.
            if (isTurboLike(state.settings.autoPlaySpeed)) {
              for (let i = 0; i < reelRefs.current.length; i++) {
                const ctrl = reelRefs.current[i]?.getReelController?.();
                if (ctrl) {
                  ctrl.lockedSpinSpeed = "auto";
                  ctrl._speedConfigCache = null;
                }
              }
            }
          }
        }

        stopReels(
          FEATURES_CONFIG.SPIN.STOP_DELAY_MANUAL,
          manualStopTension,
          isSwitchingToTurbo,
        );

        // Clear any pending forceStopTension hide timeouts from handleManualStop.
        // The onReelLanded callback (registered by stopReels) handles the visual
        // trigger when the last trigger reel lands — we just need to prevent
        // stale timeouts from interfering.
        if (manualStopTension !== null) {
          for (
            let i = manualStopTension;
            i < tensionReelRefs.current.length;
            i++
          ) {
            tensionReelRefs.current[i]?.clearAllTimeouts();
          }
        }

        if (unsubManualCleanup) unsubManualCleanup();
      };

      unsubManualCleanup = store.subscribe(onManualStoreChange);
      // Immediate check in case response already arrived
      onManualStoreChange();
    }

    return () => {
      // Reset subscription guard flag
      if (isSubscribedRef.current) {
        isSubscribedRef.current = false;
      }

      if (intervalId) {
        clearInterval(intervalId);
      }
      if (unsubscribe) {
        unsubscribe();
      }
      // Ensure manual-stop subscription is also cleaned up if still active
      if (typeof unsubManualCleanup === "function") {
        try {
          unsubManualCleanup();
        } catch (e) {}
      }

      // Enhanced cleanup - clear main connection timeout
      clearConnectionTimeouts();

      // Only close popup if we're unmounting the component entirely
      // Don't close during normal state transitions (like manual stop)
      const currentState = store.getState();
      const isUnmounting =
        !currentState.spin.isSpinning &&
        !currentState.spin.manualStop &&
        currentState.spin.spinComplete;

      if (!triggered && isUnmounting) {
        const uiState = store.getState();
        if (uiState.userInterface.connectionPopupActive) {
          dispatch(closeConnectionPopup());
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isSpinning,
    manualStop,
    spinComplete,
    stopReels,
    freezeReels,
    dispatch,
    clearConnectionTimeouts,
    clearManualStopTimeouts,
    showConnectionPopup,
    reelRefs,
  ]);

  // ============================================================================
  // AUTOPLAY STATE MEMOIZATION
  // ============================================================================

  /**
   * Memoized Autoplay State
   *
   * Consolidates all autoplay-related Redux selectors into a single memoized object.
   * Prevents unnecessary re-renders of the Autoplay Continuous Spinning Effect by
   * ensuring the effect only runs when autoplay-related state actually changes.
   *
   * Includes: autoplay active state, spin completion, remaining spins, balance, bet,
   * counter animation state, autoplay speed, win animations, current round, menu state.
   */
  const autoplayState = useMemo(() => {
    return {
      autoPlayActive,
      spinComplete,
      remainingAutoSpins,
      balance,
      bet,
      counterAnimationFinished,
      // autoPlaySpeed removed - read directly from store to prevent timeout resets on speed changes
      winAnimationsPlaying,
      criticalAnimationPlaying,
      currentRound,
      menuActive,
      anyFeatureTriggeredOrActive,
      stopOnAnyWin,
      stopOnBonusGame,
      stopOnSingleWinExceeds,
      stopOnBalanceIncreases,
      stopOnBalanceDecreases,
      singleWinExceedsAmount,
      balanceIncreasesAmount,
      balanceDecreasesAmount,
      errorPopupActive,
      infoPopupActive,
      freeRoundsCompletionPending,
    };
  }, [
    autoPlayActive,
    spinComplete,
    remainingAutoSpins,
    balance,
    bet,
    counterAnimationFinished,
    // autoPlaySpeed removed from dependencies
    winAnimationsPlaying,
    criticalAnimationPlaying,
    currentRound,
    menuActive,
    anyFeatureTriggeredOrActive,
    stopOnAnyWin,
    stopOnBonusGame,
    stopOnSingleWinExceeds,
    stopOnBalanceIncreases,
    stopOnBalanceDecreases,
    singleWinExceedsAmount,
    balanceIncreasesAmount,
    balanceDecreasesAmount,
    errorPopupActive,
    infoPopupActive,
    freeRoundsCompletionPending,
  ]);

  // ============================================================================
  // USER ACTION HANDLERS
  // ============================================================================

  /**
   * Handles manual stop action triggered by user click/spacebar/touch.
   *
   * Performs comprehensive cleanup and state management:
   * - Validates state guards (UI visibility, win animations)
   * - Checks if any reels are still winding up (defers stop until all wind-ups complete)
   * - Tracks manual stop count for turbo suggestion feature (suggests turbo after 4 stops)
   * - Sets up connection timeout popup for slow server responses
   * - Snapshot which reels haven't stopped yet for animation suppression
   * - Stops all tension animations and sounds immediately
   * - Clears all tracking state (stopped reels, tension animations, timestamps)
   * - Dispatches manual stop and autoplay stop actions
   * - Deactivates spacebar turbo if active
   *
   * Prevents duplicate stops using stopTriggeredRef guard.
   */
  const handleManualStop = useCallback(() => {
    const currentState = store.getState();

    if (currentState.userInterface.userInterfaceHidden) {
      return;
    }

    if (currentState.animations.winAnimationsPlaying) {
      return;
    }

    // --- TENSION SKIP: press during active tension skips current reel only ---
    // Each press skips one tension reel. The cascade naturally starts the next.
    if (pendingTensionRef.current) {
      // Find the currently active tension reel (has visible tension spine)
      let activeTensionReel = -1;
      for (let i = 0; i < tensionReelRefs.current.length; i++) {
        if (tensionReelRefs.current[i]?.isTensionVisible()) {
          activeTensionReel = i;
          break;
        }
      }

      if (activeTensionReel !== -1) {
        // Skip if we already force-stopped this same reel (rapid press guard)
        if (activeTensionReel === lastForceStoppedReelRef.current) {
          return;
        }
        lastForceStoppedReelRef.current = activeTensionReel;

        // Cancel this reel's pending stop timeout (it would fire after tensionDuration)
        const existingTimeout =
          reelStopTimeoutsRef.current.get(activeTensionReel);
        if (existingTimeout) {
          if (existingTimeout && typeof existingTimeout.clear === "function") {
            existingTimeout.clear();
          }
          reelStopTimeoutsRef.current.delete(activeTensionReel);
          const timeoutIdx = timeoutsRef.current.indexOf(existingTimeout);
          if (timeoutIdx > -1) {
            timeoutsRef.current.splice(timeoutIdx, 1);
          }
        }

        // Force-stop this tension reel: plays tension_out and stops the reel
        reelRefs.current[activeTensionReel]?.stopTensionSpin();
        tensionReelRefs.current[activeTensionReel]?.playTensionOut();
        activeTensionAnimationsRef.current.add(activeTensionReel);
        tensionAnimationStartTimesRef.current.set(
          activeTensionReel,
          Date.now(),
        );
        stoppedReelsRef.current.add(activeTensionReel);

        return; // Don't do full manual stop — just skip this reel
      }
    }

    // --- ANTICIPATION SKIP: a press cuts the current chain reel only ---
    // Its in / loop is cut short: the pending stop is cancelled and the reel
    // stops now. Its landing peak plays the out in full and hands the chain
    // to the next reel exactly as a natural stop does (see stopReels), so
    // each press skips one reel. A press while the out is playing, or on a
    // reel already stopping, does nothing — the full manual stop below would
    // tear into a chain that is already handing over.
    if (makeAnticipationRunner().skipActiveReel()) {
      return;
    }

    if (stopTriggeredRef.current) {
      return;
    }

    // CRITICAL: Prevent duplicate manual stops during autoplay
    // If autoplay is active and manual stop is already pending, ignore duplicate calls
    if (currentState.settings.autoPlayActive && pendingManualStopRef.current) {
      return;
    }

    // Check if any reel is still winding up
    // If so, defer manual stop until all wind-ups complete
    const reels = reelRefs.current;
    let anyReelWindingUp = false;
    for (let i = 0; i < reels.length; i++) {
      const controller = reels[i]?.getReelController();
      if (controller?.isWindingUp) {
        anyReelWindingUp = true;
        break;
      }
    }

    // If any reel is winding up, just set pending flag and wait
    if (anyReelWindingUp) {
      pendingManualStopRef.current = true;
      stopTriggeredRef.current = true;
      return; // Don't dispatch yet, let wind-ups complete
    }

    stopTriggeredRef.current = true;
    pendingManualStopRef.current = true;
    spinStartTimeRef.current = null;

    // Track manual stops for turbo suggestion feature (only if feature is enabled)
    if (FEATURES_CONFIG.TURBO_SUGGESTION.ENABLED) {
      const settingsState = store.getState().settings;
      if (settingsState.manualStopTrackingEnabled) {
        dispatch(incrementManualStopCount());
        if (settingsState.manualStopCount >= 4) {
          dispatch(setTurboSuggestionPending(true));
        }
      }
    }

    // Set connection timeout for manual stop
    const manualStopBuffer = CONNECTION_POPUP_TIMEOUTS.MANUAL_STOP_BUFFER;
    connectionTimeouts.current.manualStop = setTimeout(() => {
      const recheckState = store.getState();
      const stillNoData =
        recheckState.spin.status !== "succeeded" ||
        !recheckState.game.currentRound?.reels?.length;

      if (
        stillNoData &&
        recheckState.spin.manualStop &&
        recheckState.spin.isSpinning
      ) {
        showConnectionPopup();
      }
    }, manualStopBuffer);
    // Also track in main timeouts array for proper cleanup
    timeoutsRef.current.push(connectionTimeouts.current.manualStop);

    // Snapshot which reels haven't stopped yet for animation suppression
    try {
      const totalReels = reelRefs.current ? reelRefs.current.length : 0;
      const snapshot = new Set();
      for (let i = 0; i < totalReels; i++) {
        if (!stoppedReelsRef.current.has(i)) {
          snapshot.add(i);
        }
      }
      manualStopTargetReelsRef.current = snapshot;
    } catch (e) {}


    // Don't stop shake animation here - let it play until spin completes
    const tensionReelsArray = tensionReelRefs.current;
    for (let i = 0; i < tensionReelsArray.length; i++) {
      const tensionReel = tensionReelsArray[i];
      if (tensionReel) {
        tensionReel.forceStopTension();
      }
    }

    // Undim all reels on manual stop
    for (let i = 0; i < reelRefs.current.length; i++) {
      reelRefs.current[i]?.undimReel();
    }

    // Check if autoplay is active BEFORE clearing tracking state
    const currentAutoPlayActive = currentState.settings.autoPlayActive;

    // CRITICAL: Don't clear stoppedReelsRef during autoplay - autoplay needs it for natural stopping
    // Only clear tracking state if autoplay is NOT active (manual spin scenario)
    if (!currentAutoPlayActive) {
      // Reset all tracking state for manual spins
      stoppedReelsRef.current.clear();
      lastForceStoppedReelRef.current = -1;
      activeTensionAnimationsRef.current.clear();
      tensionAnimationStartTimesRef.current.clear();
      dimmedReelsRef.current.clear();
    } else {
      // During autoplay, only clear tension-related state, preserve stoppedReelsRef
      activeTensionAnimationsRef.current.clear();
      tensionAnimationStartTimesRef.current.clear();
      dimmedReelsRef.current.clear();
    }

    dispatch(manualSpinStop());

    // Only stop autoplay if it's NOT active (manual spin scenario)
    // If autoplay IS active, let it continue to next spin after manual stop
    if (!currentAutoPlayActive) {
      dispatch(stopAutoPlay());
    }

    if (spacebarTurboActive) {
      setSpacebarTurboActive(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, spacebarTurboActive, showConnectionPopup]);

  /**
   * Replay round finished (docs/replay-history-spec.md §4) — open the Ended
   * modal on its own, a moment after the same "everything is done, spin is
   * allowed again" moment handleSpin's own guards below already check for
   * (not spinning, win animations done, counter fully finished — no new
   * signal invented here). By design this must NOT wait for the player to
   * press Spin again; handleSpin's matching check further down stays too,
   * as a backstop for the narrow window before this timer fires.
   *
   * A replayed BONUS round runs well past its last step: the respins finish,
   * then the outro plays, then the cloud transition carries the player back
   * to base game, and only THEN is the bonus total credited to the balance
   * (see GameView's outro-transition onComplete). Opening on the last step
   * alone would cover all of that and show the summary before the player
   * ever saw the balance move — so the bonus lifecycle flags below hold the
   * timer until the player is genuinely back in the base game with the win
   * paid in.
   *
   * The LAST round of a free-rounds campaign gets the same treatment for the
   * same reason: the server reports it as "played", which runs the completion
   * sequence (the "you won X in free rounds" popup, then the 206 notice, then
   * the FR teardown that reveals the final Total Win). That's the end of the
   * campaign the player is watching, so the summary waits for it to finish —
   * exactly as it waits for the bonus outro.
   */
  useEffect(() => {
    if (!replaySessionActive || !replayFinished) return;
    if (isSpinning || winAnimationsPlaying) return;
    if (counterAnimationStarted && !counterAnimationFinished) return;
    // Bonus outro / free-rounds completion still running — the selectors above
    // are listed in this effect's deps purely so it re-runs as each clears;
    // the check itself is shared with handleSpin's backstop.
    if (!isReplayRoundSettled(store.getState())) {
      // Named, because this gate is the reason the outro used to land on top
      // of the Total Win. Each flag is listed so the log says WHICH one is
      // still holding rather than only that something is.
      // EVERY flag the predicate tests, not a selection of them.
      //
      // It used to name eight of the thirteen. A round held by one of the
      // other five printed a line with everything false — which says "held by
      // something I am not telling you about", and that is the same as no log
      // at all. The list is in the same order as isReplayRoundSettled so the
      // two can be read side by side.
      const st = store.getState();
      baseDiag("replay -> ended modal HELD", {
        pcActive: !!st.potCollection?.potCollectionActive,
        pcTriggered: !!st.potCollection?.potCollectionTriggered,
        pcOutroWipe: !!st.animations?.potCollectionOutroTransitionActive,
        critical: !!st.animations?.criticalAnimationPlaying,
        frCompletion: !!st.freeRounds?.completionPending,
        infoPopup: !!st.userInterface?.infoPopupActive,
        errorPopup: !!st.userInterface?.errorPopupActive,
        fsRoom: !!st.freeSpins?.active,
        fsTriggered: freeSpinsTriggerHolding(st),
        fsTriggerRaw: !!st.freeSpins?.triggered,
        gambleDialog: !!st.animations?.showGambleDialog,
        fsIntro: !!st.animations?.showFreeSpinIntro,
        totalWin: !!st.animations?.showFreeSpinTotalWin,
        maxWin: !!st.animations?.showFreeSpinMaxWin,
        zeroWin: !!st.animations?.showFreeSpinZeroWin,
        wipe: !!st.animations?.showTransitionDialog,
      });
      return;
    }

    baseDiag("replay -> ended modal ARMED (round settled)");

    const tickerTimeout = new TickerTimeout(() => {
      const state = store.getState();
      const isStandalone = state.replay?.isStandalone;
      if (isStandalone) {
        // Standalone replay link (§0 Stage 9): there is no History board in
        // this mode — it's never fetched, never mounted, and must never
        // appear. Only the Ended modal opens. Its round data was pre-staged
        // at boot with just bet/time (no history row exists to source from),
        // so fold in the win this playthrough actually produced — otherwise
        // the modal's Total Win / xBet render as NaN.
        const win = resolveStandaloneRoundWin(state);
        dispatch(setEndedRound({ ...(state.replay?.endedRound || {}), win }));
        dispatch(openReplayEnded());
        return;
      }
      dispatch(openReplay());
      dispatch(openReplayEnded());
    }, REPLAY_ENDED_POPUP_DELAY_MS);

    return () => tickerTimeout.clear();
  }, [
    replaySessionActive,
    replayFinished,
    isSpinning,
    winAnimationsPlaying,
    counterAnimationStarted,
    counterAnimationFinished,
    potCollectionActive,
    potCollectionTriggered,
    potCollectionOutroTransitionActive,
    criticalAnimationPlaying,
    freeRoundsCompletionPending,
    infoPopupActive,
    errorPopupActive,
    freeSpinsRoomActive,
    freeSpinsRoomTriggered,
    // freeSpinsTriggerHolding reads these two, and this function reads the
    // store directly — so without them here the modal would stay shut until
    // some other dep happened to change. Same rule as showTransitionDialog.
    showGambleDialog,
    showFreeSpinIntro,
    showFreeSpinTotalWin,
    showFreeSpinMaxWin,
    showFreeSpinZeroWin,
    // Listed for the reason stated on the predicate: nothing re-runs this
    // effect on its own, so a flag that is not a dep here can hold the modal
    // shut for ever.
    showTransitionDialog,
    dispatch,
  ]);

  /**
   * Initiates a new spin - main entry point for all spin types (manual, autoplay, turbo).
   *
   * Complete spin initialization flow:
   * - Validates state guards (UI visibility, connection popup, win animations)
   * - Rate limits to prevent spam (75ms minimum between spins)
   * - Resets all tracking state (timeouts, tension, animations, reel states)
   * - Resets counter animation finished flag for new spin
   * - Clears connection timeouts and popups
   * - Force-resets internal state of all reel controllers
   * - Checks balance and triggers fake spin if insufficient credits
   * - Dispatches API call (rgsPlay or triggerFakeSpin) with optional options
   * - Starts appropriate spin type based on current speed:
   *   * Turbo: All reels start simultaneously, triggers button rotation animation
   *   * Regular: Sequential reel starts
   * - Sets up emergency timeout fallback (5 seconds) to prevent stuck spins
   *
   * @param {Object} options - Optional parameters to pass to rgsPlay (e.g., { purchasedFeature: "bonus_buy" })
   *
   * Called by: manual clicks, autoplay, turbo mode, and first spin initialization.
   */
  const handleSpin = useCallback(
    (options = {}) => {
      const currentState = store.getState();


      if (currentState.userInterface.userInterfaceHidden) {

        return;
      }

      if (currentState.userInterface.connectionPopupActive) {

        return;
      }

      if (currentState.animations.winAnimationsPlaying) {

        return;
      }

      // Block new spin if there's an active win counter that hasn't finished
      // Prevents skipping wins via fast spacebar during win processing
      if (
        currentState.animations.counterAnimationStarted &&
        !currentState.animations.counterAnimationFinished
      ) {

        return;
      }

      // Rate limiting (75ms minimum between spins)
      const now = Date.now();
      const timeSinceLastSpin = now - (handleSpin.lastCall || 0);
      if (timeSinceLastSpin < 75) {

        return;
      }
      handleSpin.lastCall = now;

      if (isSpinning) {
        // Allow tension / anticipation skip via click/tap
        if (pendingTensionRef.current || isAnticipationChainActive()) {
          handleManualStop();
        }

        return;
      }

      // Replay round finished (docs/replay-history-spec.md §4) — show the
      // Ended modal instead of starting another spin. Checked here, after
      // every guard above has passed, so this only fires at the exact
      // moment the live game would otherwise commit to a genuinely new
      // spin (win reveal + counter fully done, nothing else blocking) —
      // the same signal the live game already trusts, not a new one.
      //
      // Also re-opens the History board underneath — handleEnterPlay closed
      // it to reveal the game while the round played, but per spec §4 the
      // board should already be there once Ended appears, so closing Ended
      // alone reveals it (matches the Enter modal's own stacking, just on
      // the way out instead of in).
      if (
        currentState.replay.sessionActive &&
        currentState.replay.replayFinished
      ) {
        // The replayed round is over: this must ALWAYS block, never fall
        // through to start a spin. While the round is still settling (bonus
        // outro, free-rounds completion — see isReplayRoundSettled) the press
        // is simply swallowed; the summary opens once it has. Falling through
        // here would run a spin whose exhausted step cursor wraps and restarts
        // the round mid-outro.
        if (!isReplayRoundSettled(currentState)) {
          return;
        }
        if (currentState.replay.isStandalone) {
          // Standalone replay link (§0 Stage 9): no History board exists in
          // this mode. Only the Ended modal — whose X and REPLAY both
          // re-launch the same round — so the player is permanently looped
          // on this one round and can never reach a live spin.
          const win = resolveStandaloneRoundWin(currentState);
          dispatch(
            setEndedRound({ ...(currentState.replay.endedRound || {}), win }),
          );
          dispatch(openReplayEnded());
          return;
        }
        dispatch(openReplay());
        dispatch(openReplayEnded());
        return;
      }

      // Reset all state for new spin
      dispatch(setCounterAnimationFinished(false));
      clearConnectionTimeouts();
      const uiState = store.getState();
      if (uiState.userInterface.connectionPopupActive) {
        dispatch(closeConnectionPopup());
      }
      suppressLandingAnimationsRef.current = false;
      suppressLandingAnimationsPerReelRef.current.clear();
      manualStopTargetReelsRef.current.clear();
      clearAllTimeouts(); // ⚠️ This clears ALL timeouts including reel start timeouts if called again
      stopTriggeredRef.current = false;
      pendingManualStopRef.current = false;
      currentSpinMethodRef.current = null;
      stoppedReelsRef.current.clear();
      lastForceStoppedReelRef.current = -1;
      pendingTensionRef.current = null;
      tensionReels.current = new Set();
      activeTensionAnimationsRef.current.clear();
      tensionAnimationStartTimesRef.current.clear();
      dimmedReelsRef.current.clear();
      tensionEffectActiveRef.current = false; // Reset effect flag for new spin

      // ── Synchronous visual cleanup before reels start ──────────────
      // Clears dim, win lines, win boxes, overlay animations, textures in ONE frame.
      // snapClearAllRef is populated by useWinController with the full cleanup function.
      for (let i = 0; i < reelRefs.current.length; i++) {
        reelRefs.current[i]?.undimReel();
      }
      if (snapClearAllRef.current) {
        snapClearAllRef.current();
      } else if (dimReelSetRef.current?.snapClear) {
        // Fallback: at minimum snap-clear the dim overlay
        dimReelSetRef.current.snapClear();
      }
      // The jackpot plates the last round lit go dark again.
      gameViewControllerRef?.current?.releaseJackpotHits?.();

      // Reset responsible gambling check flag for new spin
      if (FEATURES_CONFIG.RESPONSIBLE_GAMBLING.ENABLED) {
        responsibleGamblingCheckedRef.current = false;
      }

      // Force-reset internal state of all reel controllers
      const reels = reelRefs.current;
      for (let i = 0; i < reels.length; i++) {
        const controller = reels[i]?.getReelController();
        if (controller) {
          controller.isStopping = false;
          controller.hasStopped = false;
          controller.hasTensionThisSpin = false;
        }
      }

      // Emergency fallback timeout (30 seconds)
      // Must exceed worst-case tension cascade: 5 reels × (3.1s tension + 1.2s landing) ≈ 21.5s
      const emergencyStopTimeout = setTimeout(() => {
        // Remove this timeout from tracking since it fired naturally
        const idx = timeoutsRef.current.indexOf(emergencyStopTimeout);
        if (idx > -1) {
          timeoutsRef.current.splice(idx, 1);
        }
        if (store.getState().spin.isSpinning) {
          handleManualStop();
        }
      }, 30000);
      timeoutsRef.current.push(emergencyStopTimeout);

      // Always send API request - let API handle insufficient credits
      spinStartTimeRef.current = Date.now();
      dispatch(rgsPlay(options));

      // Start appropriate spin type based on current speed
      const currentAutoPlaySpeed = store.getState().settings.autoPlaySpeed;
      const reelsForSpin = reelRefs.current;

      // Trigger theme-appropriate animation on every spin start (manual, autoplay, turbo)
      if (playButtonRef.current) {
        if (Settings.APP.UI_THEME === "secondary") {
          // Secondary theme: rotation animation only (no scale press)
          createPlayButtonRotationAnimation(playButtonRef.current);
        } else {
          // Primary theme: scale press animation
          triggerScalePress(playButtonRef.current);
        }
      }

      if (isTurboLike(currentAutoPlaySpeed)) {
        currentSpinMethodRef.current = "turbo";
      } else {
        currentSpinMethodRef.current = "regular";
      }

      // ── Turbo is a DROP. It only becomes a spin when the server is slow ──
      //
      // Auto and quick start turning at press and stop when the answer lands.
      // Turbo must not: press, answer, fall into place is the whole feel of the
      // mode, and starting the reels at press stretched every turbo round by a
      // whole network round trip.
      //
      // So turbo stays idle for SPIN_AFTER_WAIT_MS. On a healthy connection the
      // response beats that window, the timer is cancelled and the reels drop
      // exactly as they always have — nothing spins at all. Only when the
      // server misses the window do they start turning, so a stall shows a
      // working game instead of a frozen board.
      //
      // The response effect owns both endings: see turboSpinningRef.
      if (isTurboLike(currentAutoPlaySpeed)) {
        turboSpinningRef.current = false;
        turboSpinTimerRef.current?.clear();

        const turboCfg =
          FEATURES_CONFIG.SPIN.AUTO_PLAY_SPEEDS[
            currentAutoPlaySpeed?.toUpperCase()
          ] || FEATURES_CONFIG.SPIN.AUTO_PLAY_SPEEDS.TURBO;

        turboSpinTimerRef.current = new TickerTimeout(() => {
          turboSpinTimerRef.current = null;
          // Still waiting, and still ours to start. A manual stop counts as
          // no longer ours — the stop path lands the reels from wherever they
          // are, and starting them underneath it would fight that.
          const st = store.getState();
          if (
            !st.spin.isSpinning ||
            st.spin.spinComplete ||
            st.spin.manualStop
          ) {
            return;
          }
          turboSpinningRef.current = true;
          spinStartTimeRef.current = Date.now();
          const reels = reelRefs.current;
          for (let i = 0; i < reels.length; i++) {
            reels[i]?.startSpinNoWindUp();
          }
        }, turboCfg.SPIN_AFTER_WAIT_MS ?? 300);
      } else {
        const speedConfig =
          FEATURES_CONFIG.SPIN.AUTO_PLAY_SPEEDS[
            currentAutoPlaySpeed?.toUpperCase()
          ] || FEATURES_CONFIG.SPIN.AUTO_PLAY_SPEEDS.AUTO;
        const reelStartDelay =
          speedConfig.REEL_START_DELAY !== undefined
            ? speedConfig.REEL_START_DELAY
            : FEATURES_CONFIG.SPIN.REEL_START_DELAY;
        const reelStartDelays =
          speedConfig.REEL_START_DELAYS ||
          FEATURES_CONFIG.SPIN.REEL_START_DELAYS;
        const skipWindUp = speedConfig.SKIP_WIND_UP || false;

        // Sequential or simultaneous start based on speed mode
        for (let i = 0; i < reelsForSpin.length; i++) {
          // Use non-linear per-reel delays if available, otherwise fall back to linear
          const delay = reelStartDelays
            ? reelStartDelays[i] || 0
            : i * reelStartDelay;
          if (delay === 0) {
            if (skipWindUp) {
              reelsForSpin[i]?.startSpinNoWindUp();
            } else {
              reelsForSpin[i]?.startSpin();
            }
          } else {
            const timeoutId = new TickerTimeout(() => {
              const idx = timeoutsRef.current.indexOf(timeoutId);
              if (idx > -1) {
                timeoutsRef.current.splice(idx, 1);
              }
              if (skipWindUp) {
                reelsForSpin[i]?.startSpinNoWindUp();
              } else {
                reelsForSpin[i]?.startSpin();
              }
            }, delay);
            timeoutsRef.current.push(timeoutId);
          }
        }
      }

        // After all reels finish wind-up, check if manual stop is pending.
        // Turbo skips it: its reels are deliberately still idle inside the
        // wait window, so there is no wind-up to complete and nothing to stop.
        // The response effect owns turbo's stop either way.
        if (!isTurboLike(currentAutoPlaySpeed)) {
          // Re-derive speed config for this scope (defined in the spin-start block above)
          const windUpSpeedConfig =
            FEATURES_CONFIG.SPIN.AUTO_PLAY_SPEEDS[
              currentAutoPlaySpeed?.toUpperCase()
            ] || FEATURES_CONFIG.SPIN.AUTO_PLAY_SPEEDS.AUTO;
          const reelStartDelay =
            windUpSpeedConfig.REEL_START_DELAY !== undefined
              ? windUpSpeedConfig.REEL_START_DELAY
              : FEATURES_CONFIG.SPIN.REEL_START_DELAY;
          const skipWindUp = windUpSpeedConfig.SKIP_WIND_UP || false;

          // Calculate total time: (last reel start delay) + (wind-up duration) + (safety buffer)
          const windUpDuration = FEATURES_CONFIG.SPIN.WIND_UP_DURATION;
          const lastReelStartDelay = (reelsForSpin.length - 1) * reelStartDelay;
          const totalWindUpTime = lastReelStartDelay + windUpDuration;

          // If wind-up is disabled or skipped (quick mode), check immediately
          const checkDelay =
            windUpDuration === 0 || skipWindUp
              ? 0
              : totalWindUpTime + FEATURES_CONFIG.SPIN.WIND_UP_CHECK_BUFFER;

          const checkPendingStopTimeout = new TickerTimeout(() => {
            // Remove this timeout from tracking since it fired naturally
            const idx = timeoutsRef.current.indexOf(checkPendingStopTimeout);
            if (idx > -1) {
              timeoutsRef.current.splice(idx, 1);
            }

            // If manual stop was pressed during wind-up, execute it now
            if (pendingManualStopRef.current && stopTriggeredRef.current) {
              // Track manual stops for turbo suggestion feature (only if feature is enabled)
              if (FEATURES_CONFIG.TURBO_SUGGESTION.ENABLED) {
                const settingsState = store.getState().settings;
                if (settingsState.manualStopTrackingEnabled) {
                  dispatch(incrementManualStopCount());
                  if (settingsState.manualStopCount >= 4) {
                    dispatch(setTurboSuggestionPending(true));
                  }
                }
              }

              // Set connection timeout for manual stop (STAYS as setTimeout — wall-clock server monitoring)
              const manualStopBuffer =
                CONNECTION_POPUP_TIMEOUTS.MANUAL_STOP_BUFFER;
              connectionTimeouts.current.manualStop = setTimeout(() => {
                const recheckState = store.getState();
                const stillNoData =
                  recheckState.spin.status !== "succeeded" ||
                  !recheckState.game.currentRound?.reels?.length;

                if (
                  stillNoData &&
                  recheckState.spin.manualStop &&
                  recheckState.spin.isSpinning
                ) {
                  showConnectionPopup();
                }
              }, manualStopBuffer);
              // Also track in main timeouts array for proper cleanup
              timeoutsRef.current.push(connectionTimeouts.current.manualStop);

              // Snapshot which reels haven't stopped yet for animation suppression
              try {
                const totalReels = reelRefs.current ? reelRefs.current.length : 0;
                const snapshot = new Set();
                for (let i = 0; i < totalReels; i++) {
                  if (!stoppedReelsRef.current.has(i)) {
                    snapshot.add(i);
                  }
                }
                manualStopTargetReelsRef.current = snapshot;
              } catch (e) {}


              // Don't stop shake animation here - let it play until spin completes
              const tensionReelsArray = tensionReelRefs.current;
              for (let i = 0; i < tensionReelsArray.length; i++) {
                const tensionReel = tensionReelsArray[i];
                if (tensionReel) {
                  tensionReel.forceStopTension();
                }
              }

              // Undim all reels on manual stop
              for (let i = 0; i < reelRefs.current.length; i++) {
                reelRefs.current[i]?.undimReel();
              }

              // Reset all tracking state
              stoppedReelsRef.current.clear();
              lastForceStoppedReelRef.current = -1;
              activeTensionAnimationsRef.current.clear();
              tensionAnimationStartTimesRef.current.clear();
              dimmedReelsRef.current.clear();

              dispatch(manualSpinStop());

              // Only stop autoplay if it's NOT active (manual spin scenario)
              const currentAutoPlayActive =
                store.getState().settings.autoPlayActive;
              if (!currentAutoPlayActive) {
                dispatch(stopAutoPlay());
              }

              if (spacebarTurboActive) {
                setSpacebarTurboActive(false);
              }
            }
          }, checkDelay);
          timeoutsRef.current.push(checkPendingStopTimeout);
        }

    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      dispatch,
      clearAllTimeouts,
      clearConnectionTimeouts,
      handleManualStop,
      isSpinning,

      reelRefs,
      playButtonRef,
      showConnectionPopup,
      spacebarTurboActive,
      setSpacebarTurboActive,
      tensionReelRefs,
    ],
  );

  // ── The Random Pot (GDD "Random Pot — Hold & Win Activation") ──
  //
  // The server gave Hold & Win through the Pot on this paid spin; its coins
  // are not on the board. Once the paid spin has been fully presented, in the
  // GDD's order:
  //   1. the Pot's blast (logoPotWin -> logo_blast), to its end
  //   2. a PRESENTATION spin — the ordinary spin path, but rgsPlay sends
  //      nothing (Games/Shared/randomPot) — while the logo flies over the
  //      reels (logo_reel_* + logo_lvl<n>_reel)
  //   3. the flight over, the reels stop on the awarded coins; they light as
  //      the trigger line, which opens the Hold & Win intro as a natural
  //      trigger does.
  // Once per trigger (keyed on the H&W step), never on the presentation board.
  const hwTriggered = useSelector((state) => state.holdAndWin.triggered);
  const hwRandomPot = useSelector((state) => state.holdAndWin.randomPot);
  const hwPhase = useSelector((state) => state.holdAndWin.phase);
  const hwStepId = useSelector((state) => state.holdAndWin.stepId);
  const randomPotRanRef = useRef(null);
  // Re-run when the start screen goes away (a reload onto the Pot's trigger).
  const randomPotIntroPopupActive = useSelector(selectIntroPopupActive);
  const randomPotGameShown = useSelector(selectShowGameAfterTransition);
  useEffect(() => {
    if (!hwTriggered || !hwRandomPot || hwPhase !== "intro") return;
    if (randomPotRanRef.current === hwStepId) return;
    const st = store.getState();
    if (st.spin.isSpinning || !st.spin.spinComplete) return;
    if (st.animations.winAnimationsPlaying) return;
    if ((st.game.currentRound?.win || 0) > 0 && !st.animations.counterAnimationFinished) {
      return;
    }
    if (st.game.currentRound?.randomPotPresentation) return;
    // A reload: not behind the start screen — the game must be on view.
    if (selectIntroPopupActive(st) || !selectShowGameAfterTransition(st)) return;
    randomPotRanRef.current = hwStepId;
    // GDD: "Disable gameplay input throughout the sequence" — the H&W trigger's
    // own lock (useWinController does the same when its wins present); the
    // H&W entry releases it. handleSpin below is not gated on it.
    dispatch(setUserInterfaceDisabled(true));
    dispatch(setCriticalAnimationPlaying(true));
    (async () => {
      logoPotWin();
      await gameViewControllerRef?.current?.logoBlastDone?.();
      const endFlight = beginRandomPotFlight();
      handleSpin({ randomPotPresentation: true });
      await gameViewControllerRef?.current?.playLogoReelFlight?.();
      endFlight();
    })();
  }, [
    hwTriggered,
    hwRandomPot,
    hwPhase,
    hwStepId,
    isSpinning,
    spinComplete,
    winAnimationsPlaying,
    counterAnimationFinished,
    handleSpin,
    gameViewControllerRef,
    randomPotIntroPopupActive,
    randomPotGameShown,
    dispatch,
  ]);

  // ============================================================================
  // TURBO MODE HANDLERS (Spacebar-activated)
  // ============================================================================

  /**
   * Activates turbo mode when spacebar/button is held (after 200ms hold duration).
   *
   * Activation flow:
   * - Validates state guards (UI visibility, win animations, autoplay not active)
   * - Stops current spin if one is in progress (via handleManualStop)
   * - Stores previous speed for restoration when turbo deactivates
   * - Sets turbo speed in Redux store
   * - Activates turbo state (spacebarTurboActive = true)
   * - Resets spinComplete flag to allow first turbo spin
   *
   * Note: Can be activated during counter animations - continuous effect handles waiting.
   * The actual first spin is triggered by the Turbo Activation Effect.
   */
  const startSpacebarTurbo = useCallback(() => {
    const currentState = store.getState();
    if (currentState.userInterface.userInterfaceHidden) {
      return;
    }

    // Allow tension / anticipation skip even during autoplay — spacebar/click
    // skips the current tension or anticipation reel
    if ((pendingTensionRef.current || isAnticipationChainActive()) && isSpinning) {
      handleManualStop();
      return;
    }

    // CRITICAL: Block turbo activation during autoplay
    if (currentState.settings.autoPlayActive) {
      return;
    }

    if (currentState.animations.winAnimationsPlaying) {
      return;
    }

    // Allow turbo to be activated even during counter animation
    // The continuous effect will handle waiting for counter to finish

    // Read from ref to avoid stale closure — this callback may fire from a keyboard
    // event before React re-renders with the latest useState value.
    if (currentState.settings.autoPlayActive || spacebarTurboActiveRef.current) {
      return;
    }

    if (isSpinning) {
      handleManualStop();
      return;
    }

    // Determine which turbo speed to engage — respect the button's current setting
    const currentSettingsSpeed = store.getState().settings.autoPlaySpeed;
    const turboSpeed = isSuperTurbo(currentSettingsSpeed) ? "super_turbo" : "turbo";

    // ALWAYS save current speed so release restores exactly what was active.
    // If turbo button is toggled ON (speed="turbo"), this saves "turbo" — 
    // so releasing spacebar keeps the button's persistent turbo state intact.
    // Previously, a !isTurboLike() guard here skipped the save, causing release
    // to restore a stale "auto" from a prior cycle and wipe the turbo toggle.
    previousSpeedRef.current = currentSettingsSpeed;

    // Clear stale deactivation flag from a previous stop's 150ms timer.
    // Without this, rapid stop→start within 150ms causes the Activation Effect
    // to see turboDeactivatingRef=true and skip the first spin entirely.
    turboDeactivatingRef.current = false;

    setSpacebarTurboActive(true);

    if (!isSpinning && spinComplete) {
      dispatch(setAutoplaySpeed(turboSpeed));
    }

    // Skip setSpinComplete(false) when counter is active (turbo activated during win display).
    const counterIsActive =
      store.getState().animations.counterAnimationStarted &&
      !store.getState().animations.counterAnimationFinished;

    if (!counterIsActive) {
      dispatch(setSpinComplete(false));
    }
  }, [
    dispatch,
    setSpacebarTurboActive,
    isSpinning,
    spinComplete,
    handleManualStop,
  ]);

  /**
   * Deactivates turbo mode when spacebar/button is released.
   *
   * Deactivation flow:
   * - Validates state guards (UI visibility, autoplay not active)
   * - Clears all pending turbo activation flags
   * - Clears all tracked timeouts
   * - Sets turbo deactivating flag to prevent race conditions
   * - Deactivates turbo state (spacebarTurboActive = false)
   *
   * Speed restoration logic:
   * - If spin in progress: Defers speed restoration (sets turboPendingStopRef)
   * - If no spin active: Immediately restores previous speed
   * - Clears deactivating flag after 150ms delay
   *
   * The Spin Complete Cleanup Effect handles pending speed changes.
   */
  const stopSpacebarTurbo = useCallback(() => {
    // DEACTIVATION IS UNCONDITIONAL.
    // The user physically released the key/button — turbo MUST stop regardless of
    // UI state (hidden, autoplay, popups). Guards only belong on activation.

    if (turboPendingActivationRef.current) {
      turboPendingActivationRef.current = false;
    }

    // Use REF (synchronous mirror) instead of closure value.
    if (!spacebarTurboActiveRef.current) {
      return;
    }



    turboDeactivatingRef.current = true;
    setSpacebarTurboActive(false);
    // NOTE: Do NOT call clearAllTimeouts() here.
    // It kills reel-stop timeouts and the emergency 15s fallback from handleSpin,
    // causing an infinite spin race condition when spacebar is released mid-spin.
    // Turbo continuous effect cleans up its own timeouts via React cleanup functions.

    // Read isSpinning and autoPlaySpeed from the store — closures can be stale.
    const currentState = store.getState();
    const currentIsSpinning = currentState.spin.isSpinning;
    const currentAutoPlaySpeed = currentState.settings.autoPlaySpeed;
    if (currentIsSpinning && isTurboLike(currentAutoPlaySpeed)) {
      turboPendingStopRef.current = true;
    } else {
      dispatch(setAutoplaySpeed(previousSpeedRef.current));
      turboPendingStopRef.current = false;

      new TickerTimeout(() => {
        turboDeactivatingRef.current = false;
      }, 150);
    }
  }, [
    dispatch,
    setSpacebarTurboActive,
  ]);

  // ============================================================================
  // EFFECTS - TURBO MODE
  // ============================================================================

  /**
   * Turbo Activation Effect
   *
   * Handles the first turbo spin when spacebar turbo mode is activated.
   *
   * Flow:
   * - Rate limits activation attempts (tracks last activation time)
   * - Reads current state directly from store to avoid stale closures
   * - Validates conditions (turbo active, not spinning, autoplay not active, not deactivating)
   * - Sets turbo speed in Redux if not already set
   * - Checks balance before starting spin
   * - Triggers first turbo spin via handleSpin (which includes rotation animation)
   *
   * Only handles the FIRST spin - subsequent spins are managed by Turbo Continuous Effect.
   */
  useEffect(() => {
    const now = Date.now();
    lastActionRef.current.turboActivation = now;

    // Read current state to avoid circular dependencies
    const currentState = store.getState();
    const currentIsSpinning = currentState.spin.isSpinning;
    const currentAutoPlayActive = currentState.settings.autoPlayActive;
    const currentAutoPlaySpeed = currentState.settings.autoPlaySpeed;
    const currentSpinComplete = currentState.spin.spinComplete;
    if (turboDeactivatingRef.current) {
      return;
    }

    if (!spacebarTurboActive || currentIsSpinning || currentAutoPlayActive) {
      return;
    }

    // Set turbo speed if not already set — respect the button's current setting
    const currentSettingsSpeed = store.getState().settings.autoPlaySpeed;
    const targetTurboSpeed = isSuperTurbo(currentSettingsSpeed) ? "super_turbo" : "turbo";
    if (!isTurboLike(currentAutoPlaySpeed)) {
      dispatch(setAutoplaySpeed(targetTurboSpeed));
      return;
    }

    // Only start first turbo spin, continuous effect handles subsequent
    if (currentSpinComplete) {
      return;
    }

    // Allow turbo regardless of balance - API will handle insufficient credits
    handleSpin();
  }, [spacebarTurboActive, dispatch, stopSpacebarTurbo, handleSpin]);

  /**
   * Turbo Continuous Spinning Effect
   *
   * Handles all turbo spins after the first one (which is handled by Turbo Activation Effect).
   *
   * Flow for non-winning rounds:
   * - Rate limits to prevent rapid multiple spins (200ms minimum)
   * - Validates conditions (turbo active, spin complete, no autoplay, no manual stop, no win animations)
   * - Checks balance before spinning
   * - Waits 75ms delay for visual pacing
   * - Re-checks conditions before spinning (state may have changed)
   * - Triggers next spin via handleSpin (includes rotation animation)
   *
   * Flow for winning rounds:
   * - Waits for counter animation to complete (counterAnimationFinished = true)
   * - Validates all conditions again before spinning
   * - Triggers next spin immediately (no delay - counter display already provides timing)
   *
   * Rotation animation triggers inside handleSpin when reels start for perfect synchronization.
   * Handles cleanup of timeouts properly to prevent accumulation.
   */
  useEffect(() => {
    const now = Date.now();

    // Read current state once for all checks
    const currentState = store.getState();

    // Rate limiting to prevent rapid multiple turbo spins
    // For super-turbo wins, skip rate limit when counterAnimationFinished triggers re-fire
    // (the 100ms flash duration is shorter than the 200ms rate limit window)
    const isSuperTurboWinRefire =
      currentState.settings.autoPlaySpeed === "super_turbo" &&
      counterAnimationFinished &&
      currentState.game.currentRound?.win > 0;

    const timeSinceLastTurbo =
      now - (lastActionRef.current.turboContinuous || 0);
    if (timeSinceLastTurbo < 200 && !isSuperTurboWinRefire) {
      return;
    }

    lastActionRef.current.turboContinuous = now;

    const currentAutoPlayActive = currentState.settings.autoPlayActive;
    const currentManualStop = currentState.spin.manualStop;
    const currentSpinComplete = currentState.spin.spinComplete;
    const currentAutoPlaySpeed = currentState.settings.autoPlaySpeed;
    const currentCounterAnimationFinished =
      currentState.animations.counterAnimationFinished;
    const currentWinAnimationsPlaying =
      currentState.animations.winAnimationsPlaying;

    // CRITICAL: Always check autoplay state - turbo should NEVER run during autoplay
    if (currentAutoPlayActive) {
      // Force stop turbo if somehow still active during autoplay
      if (spacebarTurboActive) {
        setSpacebarTurboActive(false);
        dispatch(setAutoplaySpeed("auto"));
      }
      return;
    }

    // Block turbo spins if popups are active
    const currentErrorPopupActive = currentState.userInterface.errorPopupActive;
    const currentInfoPopupActive = currentState.userInterface.infoPopupActive;

    if (
      !spacebarTurboActive ||
      currentManualStop ||
      !currentSpinComplete ||
      currentWinAnimationsPlaying ||
      currentErrorPopupActive ||
      currentInfoPopupActive
    ) {
      return;
    }

    // Wait for activation effect to set turbo speed
    if (!isTurboLike(currentAutoPlaySpeed)) {
      return;
    }

    // ── Unified feature stop ──────────────────────────────────────────────
    // Autoplay has gated on selectAnyFeatureTriggeredOrActive since it was
    // written; turbo only ever knew about Pot Collection — a flag this game
    // never raises. So a free-spins trigger (freeSpins.triggered) left turbo
    // completely unguarded, and the held spacebar fired the next spin straight
    // over the trigger sequence.
    if (isPresentationHoldingScreen(currentState)) {
      baseDiag("turbo held", {
        critical: currentState.animations?.criticalAnimationPlaying,
        feature: selectAnyFeatureTriggeredOrActive(currentState),
      });
      return;
    }

    // ── Something owns the screen ─────────────────────────────────────────
    // The mystery reveal raises criticalAnimationPlaying on rounds that PAY
    // NOTHING, so no counter guard ever holds them: the win presentation never
    // starts, counterAnimationStarted stays false, and every check above
    // passes. In AUTO the 250ms delay hid it; turbo waits 150ms and super
    // turbo 75ms, which lands the next spin inside the anticipation.
    //
    // That is the deadlock the player sees. The reveal is asynchronous and
    // holds mysteryRunningRef for its whole length, so the NEXT round's reveal
    // finds the ref still set, returns, and never publishes its round id — the
    // one thing the win presentation waits for. Nothing moves again.


    // Allow turbo continuous regardless of balance - API will handle insufficient credits
    const currentRoundWin = currentState.game.currentRound?.win;

    if (!currentRoundWin) {
      // Use correct speed config for current mode
      const speedConfig = currentAutoPlaySpeed === "super_turbo"
        ? FEATURES_CONFIG.SPIN.AUTO_PLAY_SPEEDS.SUPER_TURBO
        : FEATURES_CONFIG.SPIN.AUTO_PLAY_SPEEDS.TURBO;

      // Clear win visuals immediately — don't wait for SPIN_DELAY
      if (snapClearAllRef.current) snapClearAllRef.current();

      const timeoutId = new TickerTimeout(() => {
        // Remove this timeout from tracking since it fired naturally
        const idx = timeoutsRef.current.indexOf(timeoutId);
        if (idx > -1) {
          timeoutsRef.current.splice(idx, 1);
        }

        // Re-check conditions before spinning
        const recheckState = store.getState();
        const recheckSpacebarTurboActive = spacebarTurboActiveRef.current;

        // CRITICAL: Double-check autoplay state before spinning
        if (recheckState.settings.autoPlayActive) {
          if (recheckSpacebarTurboActive) {
            setSpacebarTurboActive(false);
            dispatch(setAutoplaySpeed("auto"));
          }
          return;
        }

        if (
          recheckSpacebarTurboActive &&
          !recheckState.settings.autoPlayActive &&
          !recheckState.spin.manualStop &&
          isTurboLike(recheckState.settings.autoPlaySpeed) &&
          !turboDeactivatingRef.current &&
          !recheckState.userInterface.errorPopupActive &&
          !recheckState.userInterface.infoPopupActive &&
          // Raised AFTER this spin was scheduled — see the predicate.
          !isPresentationHoldingScreen(recheckState)
        ) {
          // Rotation animation now triggers inside handleSpin when reels start
          handleSpin();
        }
      }, speedConfig.SPIN_DELAY);

      timeoutsRef.current.push(timeoutId);

      // Return cleanup function at effect level
      return () => {
        timeoutId.clear();
        const index = timeoutsRef.current.indexOf(timeoutId);
        if (index > -1) {
          timeoutsRef.current.splice(index, 1);
        }
      };
    } else {
      // Wait for counter to finish, then use same SPIN_DELAY as non-winning
      if (currentCounterAnimationFinished) {
        const finalCheckState = store.getState();
        if (finalCheckState.settings.autoPlayActive) {
          if (spacebarTurboActive) {
            setSpacebarTurboActive(false);
            dispatch(setAutoplaySpeed("auto"));
          }
          return;
        }

        // Win round: counterAnimationFinished already provides correct timing via unblockDelay.
        // Use SPIN_DELAY so WinDisplay/WinText have at least one frame to render before next spin.
        // Safe now that loop control is deterministic (loop=false holds last frame — no overshoot).
        const currentAutoPlaySpeed = finalCheckState.settings.autoPlaySpeed;
        const speedConfig = currentAutoPlaySpeed === "super_turbo"
          ? FEATURES_CONFIG.SPIN.AUTO_PLAY_SPEEDS.SUPER_TURBO
          : FEATURES_CONFIG.SPIN.AUTO_PLAY_SPEEDS.TURBO;

        // Don't clear win visuals immediately — let WinText/WinDisplay stay visible
        // for SPIN_DELAY so the player can see the win amount flash before next spin.
        // handleSpin() calls snapClearAll internally before starting reels.

        const winTimeoutId = new TickerTimeout(() => {
          const idx = timeoutsRef.current.indexOf(winTimeoutId);
          if (idx > -1) timeoutsRef.current.splice(idx, 1);

          const recheckState2 = store.getState();
          if (recheckState2.settings.autoPlayActive) {
            if (spacebarTurboActiveRef.current) {
              setSpacebarTurboActive(false);
              dispatch(setAutoplaySpeed("auto"));
            }
            return;
          }

          if (
            spacebarTurboActiveRef.current &&
            !recheckState2.settings.autoPlayActive &&
            !recheckState2.spin.manualStop &&
            isTurboLike(recheckState2.settings.autoPlaySpeed) &&
            !turboDeactivatingRef.current &&
            !recheckState2.userInterface.errorPopupActive &&
            !recheckState2.userInterface.infoPopupActive &&
            !isPresentationHoldingScreen(recheckState2)
          ) {
            handleSpin();
          }
        }, speedConfig.SPIN_DELAY);

        timeoutsRef.current.push(winTimeoutId);

        return () => {
          winTimeoutId.clear();
          const index = timeoutsRef.current.indexOf(winTimeoutId);
          if (index > -1) timeoutsRef.current.splice(index, 1);
        };
      }

      return () => {};
    }
  }, [
    spacebarTurboActive,
    setSpacebarTurboActive,
    spinComplete,
    counterAnimationFinished,
    winAnimationsPlaying,
    // Both guards above are read from the store, but they must also be DEPS:
    // the effect has to run again when they clear, or turbo stops for the rest
    // of the session at the first mystery it meets.
    criticalAnimationPlaying,
    anyFeatureTriggeredOrActive,
    dispatch,
    handleSpin,
    snapClearAllRef,
  ]);

  /**
   * Turbo Stop on Win Animation Start Effect
   *
   * Automatically stops turbo mode when BIG win animations begin (BIG_WIN, MEGA_WIN, SUPER_WIN, EPIC_WIN).
   *
   * Important distinction:
   * - Counter animations (small wins): Turbo continues and waits for counter to finish
   * - Win animations (big celebrations): Turbo stops completely, user must restart manually
   *
   * Implementation:
   * - Tracks previous animation state to detect START transition (false → true)
   * - Only stops turbo on the transition to avoid stopping on state resets
   * - Calls stopSpacebarTurbo() which handles cleanup and speed restoration
   *
   * This ensures users can't skip big win celebrations with turbo mode.
   */
  const previousAnimationFlagsRef = useRef({
    counterAnimationStarted: false,
    winAnimationsPlaying: false,
  });

  useEffect(() => {
    const winAnimationsJustStarted =
      winAnimationsPlaying &&
      !previousAnimationFlagsRef.current.winAnimationsPlaying;

    // Only lift the spacebar hold for win animations — don't restore speed.
    // The turbo button's speed setting (turbo/super_turbo) is persistent and
    // should survive win animations. stopSpacebarTurbo() would reset speed
    // back to previousSpeedRef, wiping the button's toggle state.
    if (winAnimationsJustStarted && spacebarTurboActive) {
      setSpacebarTurboActive(false);
      turboPendingActivationRef.current = false;
    }

    previousAnimationFlagsRef.current = {
      counterAnimationStarted,
      winAnimationsPlaying,
    };
  }, [
    counterAnimationStarted,
    winAnimationsPlaying,
    spacebarTurboActive,
    setSpacebarTurboActive,
  ]);

  /**
   * Safety Effect: Stop turbo when error/info popups appear or free rounds complete
   *
   * Prevents turbo spins from continuing when critical popups are displayed.
   * Similar to how turbo stops for win animations, this ensures users can't
   * bypass important messages (like free rounds ending) by holding spacebar.
   *
   * CRITICAL: Also stops turbo when free rounds completion is pending, BEFORE popup opens.
   * This prevents the user from triggering a spin past the last free round during the
   * animation wait period (between last round completing and popup showing).
   *
   * Tracks popup state transitions to detect when they open (false → true).
   * Only stops turbo on the transition to avoid stopping on state resets.
   */
  const previousPopupFlagsRef = useRef({
    errorPopupActive: false,
    infoPopupActive: false,
    freeRoundsCompletionPending: false,
  });

  useEffect(() => {
    const errorPopupJustOpened =
      errorPopupActive && !previousPopupFlagsRef.current.errorPopupActive;
    const infoPopupJustOpened =
      infoPopupActive && !previousPopupFlagsRef.current.infoPopupActive;
    const freeRoundsCompletionJustStarted =
      freeRoundsCompletionPending &&
      !previousPopupFlagsRef.current.freeRoundsCompletionPending;

    // Stop turbo if any popup just opened OR free rounds completion just started
    if (
      (errorPopupJustOpened ||
        infoPopupJustOpened ||
        freeRoundsCompletionJustStarted) &&
      spacebarTurboActive
    ) {
      stopSpacebarTurbo();
    }

    previousPopupFlagsRef.current = {
      errorPopupActive,
      infoPopupActive,
      freeRoundsCompletionPending,
    };
  }, [
    errorPopupActive,
    infoPopupActive,
    freeRoundsCompletionPending,
    spacebarTurboActive,
    stopSpacebarTurbo,
  ]);

  /**
   * Safety Effect: Stop turbo when ANY feature triggers
   *
   * When a trigger flag goes false → true, turbo must eject immediately so the
   * intro popup can display. Without this, turbo fires the next spin before the
   * trigger is handled, causing the API to return flow.state=closed and the
   * trigger to be silently lost.
   *
   * This used to watch potCollectionTriggered alone, which this game never
   * raises — so a free-spins trigger caught in turbo ejected nothing. It now
   * watches the same selector autoplay does, so a feature added later is
   * covered in both places by editing selectAnyFeatureTriggeredOrActive only.
   */
  useEffect(() => {
    if (anyFeatureTriggeredOrActive && spacebarTurboActive) {
      stopSpacebarTurbo();
    }
  }, [anyFeatureTriggeredOrActive, spacebarTurboActive, stopSpacebarTurbo]);

  /**
   * Safety Effect: Force stop turbo if autoplay becomes active
   *
   * This is a critical safety net to prevent turbo from running during autoplay.
   * If turbo somehow becomes active while autoplay is active (race condition, edge case),
   * this effect will immediately stop it.
   */
  useEffect(() => {
    if (autoPlayActive && spacebarTurboActive) {
      setSpacebarTurboActive(false);
      const currentState = store.getState();
      if (isTurboLike(currentState.settings.autoPlaySpeed)) {
        dispatch(setAutoplaySpeed("auto"));
      }
    }
  }, [autoPlayActive, spacebarTurboActive, setSpacebarTurboActive, dispatch]);

  // ============================================================================
  // EFFECTS - AUTOPLAY MODE
  // ============================================================================

  /**
   * Autoplay Continuous Spinning Effect
   *
   * Manages continuous autoplay spins with proper timeout tracking and state management.
   *
   * Critical synchronization:
   * - Waits for ALL reels to mechanically stop (stoppedReelsRef.size === totalReels)
   * - Waits for ALL timeouts to clear (timeoutsRef.length === 0)
   * - Uses autoplayProcessingRef flag to prevent duplicate timeout creation
   *
   * Flow for non-winning rounds:
   * - Validates conditions (autoplay active, spin complete, app active, menu inactive)
   * - Checks balance and remaining spins
   * - Creates timeout with speed-specific delay (auto/quick/turbo)
   * - Decrements remaining spins and triggers next spin
   * - Properly tracks and cleans up timeout
   *
   * Flow for winning rounds:
   * - Waits for counter animation to complete (counterAnimationFinished = true)
   * - Prevents race condition on fresh autoplay initialization
   * - Decrements remaining spins and triggers next spin
   *
   * Handles pause/resume on app inactivity and menu states.
   */
  useEffect(() => {
    const {
      autoPlayActive,
      spinComplete,
      remainingAutoSpins,
      balance,
      counterAnimationFinished,
      // autoPlaySpeed removed - read directly from store when needed
      winAnimationsPlaying,
      criticalAnimationPlaying,
      currentRound,
      menuActive,
      anyFeatureTriggeredOrActive,
      stopOnAnyWin,
      stopOnBonusGame,
      stopOnSingleWinExceeds,
      stopOnBalanceIncreases,
      stopOnBalanceDecreases,
      singleWinExceedsAmount,
      balanceIncreasesAmount,
      balanceDecreasesAmount,
      errorPopupActive,
      infoPopupActive,
      freeRoundsCompletionPending,
    } = autoplayState;

    if (!autoPlayActive || !spinComplete) {
      // Reset flag only when autoplay stops (not when spin starts)
      // Flag persists until timeout fires to prevent duplicate timeouts
      if (!autoPlayActive && autoplayProcessingRef.current) {
        autoplayProcessingRef.current = false;
        autoplayStartBalanceRef.current = null; // Reset start balance when autoplay stops
      }
      return;
    }

    // Reset processing flag when spin completes (before scheduling next spin)
    if (autoplayProcessingRef.current) {
      autoplayProcessingRef.current = false;
    }

    // Capture starting balance on first spin of autoplay session
    if (autoplayStartBalanceRef.current === null) {
      autoplayStartBalanceRef.current = balance;
    }

    // Pause when app/tab is inactive
    if (!isActive) {
      if (autoplayProcessingRef.current) {
        autoplayProcessingRef.current = false;
      }
      return;
    }

    // Prevent duplicate processing
    if (autoplayProcessingRef.current) {
      return;
    }

    // Wait for win animations to complete
    if (winAnimationsPlaying) {
      if (autoplayProcessingRef.current) {
        autoplayProcessingRef.current = false;
      }
      return;
    }

    // ── Something owns the screen ─────────────────────────────────────────
    // winAnimationsPlaying only covers CELEBRATIONS. The mystery reveal holds
    // the board on rounds that pay nothing at all, where no counter and no win
    // flag is ever raised — so this is the only thing standing between it and
    // the next spin. AUTO's 250ms delay masked it; in turbo the delay is 16ms,
    // which fires the spin inside the anticipation and deadlocks the reveal.
    if (criticalAnimationPlaying) {
      baseDiag("autoplay held — criticalAnimationPlaying");
      if (autoplayProcessingRef.current) {
        autoplayProcessingRef.current = false;
      }
      return; // Pause, don't stop — the reveal clears the flag and we resume.
    }

    // Pause autoplay when error popup or info popup is active
    // This allows autoplay to resume after popups close (e.g., error 205 free rounds flow)
    if (errorPopupActive || infoPopupActive) {
      if (autoplayProcessingRef.current) {
        autoplayProcessingRef.current = false;
      }
      return; // Pause, don't stop
    }

    // Pause when free rounds completion flow is active
    // This prevents autoplay from continuing past the completion popups
    // Free rounds themselves continue in autoplay, but completion requires user interaction
    if (freeRoundsCompletionPending) {
      if (autoplayProcessingRef.current) {
        autoplayProcessingRef.current = false;
      }
      return; // Pause, don't stop
    }

    // Pause when menu is active
    if (menuActive) {
      if (autoplayProcessingRef.current) {
        autoplayProcessingRef.current = false;
      }
      return;
    }

    // ── Unified feature stop ──────────────────────────────────────────────
    // Stop autoplay during ANY feature game (triggered or active).
    // Saves remaining spins so GameView can resume autoplay after the
    // feature's transition completes via dispatch(resumeAutoPlay(savedCount)).
    // When adding new features, add their state to selectAnyFeatureTriggeredOrActive
    // in selectors.js — no changes needed here.
    if (anyFeatureTriggeredOrActive) {
      // Check responsible gambling: stop on bonus game (permanent stop, no resume)
      if (
        FEATURES_CONFIG.RESPONSIBLE_GAMBLING.ENABLED &&
        !responsibleGamblingCheckedRef.current &&
        stopOnBonusGame
      ) {
        responsibleGamblingCheckedRef.current = true;
        dispatch(stopAutoPlay());
        dispatch(clearAutoplayPausedSpins()); // No resume after responsible gambling stop
        resetResponsibleGamblingSettings();
        autoplayProcessingRef.current = false;
        autoplayStartBalanceRef.current = null;
        return;
      }

      // Pause autoplay: saves remaining spins in Redux for GameView to resume later
      baseDiag("autoplay PAUSED for feature", {
        fsTriggered: store.getState().freeSpins?.triggered,
        fsActive: store.getState().freeSpins?.active,
        featurePhase: store.getState().game?.featureState?.phase ?? null,
        remaining: remainingAutoSpins,
      });
      dispatch(pauseAutoPlayForFeature());
      autoplayProcessingRef.current = false;
      return;
    }

    autoplayProcessingRef.current = true;

    // Allow autoplay to continue regardless of balance - API will handle insufficient credits
    if (remainingAutoSpins <= 0 && remainingAutoSpins !== Infinity) {
      dispatch(stopAutoPlay());
      autoplayProcessingRef.current = false;
      return;
    }

    const currentRoundWin = currentRound?.win;

    // Check responsible gambling conditions
    // Check immediately when spin completes, before scheduling next spin
    if (
      FEATURES_CONFIG.RESPONSIBLE_GAMBLING.ENABLED &&
      !responsibleGamblingCheckedRef.current
    ) {
      // 1. Stop on any win
      if (stopOnAnyWin && currentRoundWin && currentRoundWin > 0) {
        responsibleGamblingCheckedRef.current = true;
        dispatch(stopAutoPlay());
        resetResponsibleGamblingSettings();
        autoplayProcessingRef.current = false;
        autoplayStartBalanceRef.current = null;
        return;
      }

      // 2. Stop on bonus game (6+ coins detected) - fallback check
      // Primary check is above before Hold & Win pause
      const hasBonusGameTrigger = detectHoldAndWinTrigger(currentRound);
      if (stopOnBonusGame && hasBonusGameTrigger) {
        responsibleGamblingCheckedRef.current = true;
        dispatch(stopAutoPlay());
        resetResponsibleGamblingSettings();
        autoplayProcessingRef.current = false;
        autoplayStartBalanceRef.current = null;
        return;
      }

      // 3. Stop if single win exceeds amount
      if (
        stopOnSingleWinExceeds &&
        singleWinExceedsAmount &&
        currentRoundWin &&
        currentRoundWin > 0
      ) {
        // Convert display value (e.g., "1000.50") to wallet value (e.g., 100050)
        const winExceedsThresholdStr = parseAutoplayInputFromStore(
          singleWinExceedsAmount,
        );
        const winExceedsThreshold = Number(winExceedsThresholdStr);
        if (
          !isNaN(winExceedsThreshold) &&
          currentRoundWin > winExceedsThreshold
        ) {
          responsibleGamblingCheckedRef.current = true;
          dispatch(stopAutoPlay());
          resetResponsibleGamblingSettings();
          autoplayProcessingRef.current = false;
          autoplayStartBalanceRef.current = null;
          return;
        }
      }

      // 4. Stop if balance increases by amount
      if (
        stopOnBalanceIncreases &&
        balanceIncreasesAmount &&
        autoplayStartBalanceRef.current !== null
      ) {
        // Convert display value (e.g., "1000.50") to wallet value (e.g., 100050)
        const increaseThresholdStr = parseAutoplayInputFromStore(
          balanceIncreasesAmount,
        );
        const increaseThreshold = Number(increaseThresholdStr);
        const balanceIncrease = balance - autoplayStartBalanceRef.current;
        if (!isNaN(increaseThreshold) && balanceIncrease >= increaseThreshold) {
          responsibleGamblingCheckedRef.current = true;
          dispatch(stopAutoPlay());
          resetResponsibleGamblingSettings();
          autoplayProcessingRef.current = false;
          autoplayStartBalanceRef.current = null;
          return;
        }
      }

      // 5. Stop if balance decreases by amount
      if (
        stopOnBalanceDecreases &&
        balanceDecreasesAmount &&
        autoplayStartBalanceRef.current !== null
      ) {
        // Convert display value (e.g., "1000.50") to wallet value (e.g., 100050)
        const decreaseThresholdStr = parseAutoplayInputFromStore(
          balanceDecreasesAmount,
        );
        const decreaseThreshold = Number(decreaseThresholdStr);
        const balanceDecrease = autoplayStartBalanceRef.current - balance;
        if (!isNaN(decreaseThreshold) && balanceDecrease >= decreaseThreshold) {
          responsibleGamblingCheckedRef.current = true;
          dispatch(stopAutoPlay());
          resetResponsibleGamblingSettings();
          autoplayProcessingRef.current = false;
          autoplayStartBalanceRef.current = null;
          return;
        }
      }

      responsibleGamblingCheckedRef.current = true;
    }

    if (!currentRoundWin) {
      // Read speed directly from store to avoid effect re-runs on speed changes
      const currentAutoPlaySpeed = store.getState().settings.autoPlaySpeed;

      const speedConfig =
        FEATURES_CONFIG.SPIN.AUTO_PLAY_SPEEDS[
          currentAutoPlaySpeed?.toUpperCase()
        ] || FEATURES_CONFIG.SPIN.AUTO_PLAY_SPEEDS.AUTO;

      // Use turbo spin delay for turbo-like speeds to allow landing animations
      // For other speeds, use the configured AUTO_PLAY_DELAY
      const delay =
        isTurboLike(currentAutoPlaySpeed)
          ? speedConfig.SPIN_DELAY
          : speedConfig.AUTO_PLAY_DELAY;

      const timeoutId = new TickerTimeout(() => {
        // Remove this timeout from tracking since it fired naturally
        const idx = timeoutsRef.current.indexOf(timeoutId);
        if (idx > -1) {
          timeoutsRef.current.splice(idx, 1);
        }

        // Same re-check turbo now makes, and for the same reason: AUTO_PLAY_DELAY
        // is 16ms in turbo, so this fires while the reveal that was raised on
        // this very tick is still opening.
        if (isPresentationHoldingScreen(store.getState())) {
          baseDiag("autoplay spin WITHHELD at fire", {
            critical: store.getState().animations?.criticalAnimationPlaying,
            feature: selectAnyFeatureTriggeredOrActive(store.getState()),
          });
          autoplayProcessingRef.current = false;
          return;
        }
        // ── LET THE BOARD FINISH LANDING FIRST ──
        //
        // This is the NON-WINNING path, and it used to fire on the delay alone.
        // The next spin clears track 0 — the same track the landing bounce is
        // playing on — so a bounce still in the air was cut off and the round
        // ended mid-animation. Always the last reel, because the reels stop
        // 200ms apart and only the final one is still moving when the delay
        // expires. Reported 2026-09-10.
        //
        // The winning path does not need this: it goes through the win
        // presentation, which waits on the same signal before it starts.
        //
        // Costs NOTHING when there is nothing to wait for — a board with no
        // landing clips, or a turbo spin where landings are switched off
        // entirely, resolves on the next microtask. So turbo autoplay keeps its
        // 16ms cadence exactly as it is.
        waitForBoardToSettle(
          overlayReelRefs.current?.map((r) => r?.getOverlayReelController?.()),
          {
            fallbackMs: delay,
          },
        ).then(() => {
          // Re-checked on the far side of the wait. The wait is real time, and
          // autoplay can be stopped, paused or overtaken by a reveal inside it —
          // firing a spin off a decision made before it would be the same class
          // of bug the withheld-at-fire check above exists for.
          const st = store.getState();
          if (
            !st.settings.autoPlayActive ||
            isPresentationHoldingScreen(st)
          ) {
            baseDiag("autoplay spin WITHHELD after settle", {
              autoPlayActive: st.settings.autoPlayActive,
              critical: st.animations?.criticalAnimationPlaying,
            });
            autoplayProcessingRef.current = false;
            return;
          }
          baseDiag("autoplay -> spin", { remaining: remainingAutoSpins });
          dispatch(decrementRemainingAutoSpins());
          handleSpin();
          // Don't reset autoplayProcessingRef.current here - it will be reset when spinComplete becomes true
        });
      }, delay);

      // Track timeout to prevent accumulation
      timeoutsRef.current.push(timeoutId);

      // Cleanup: Only clear timeout if it hasn't fired yet
      // Flag persists across effect re-runs to prevent duplicate timeouts
      return () => {
        timeoutId.clear();
        const index = timeoutsRef.current.indexOf(timeoutId);
        if (index > -1) {
          timeoutsRef.current.splice(index, 1);
        }
      };
    } else {
      // Winning round: Wait for counter animation to complete AND win animations to finish
      // Same check as spacebar turbo - prevents triggering next spin while win lines/animations are still playing
      const animationsComplete =
        counterAnimationFinished && !winAnimationsPlaying;

      if (animationsComplete) {
        // Prevent race condition on fresh autoplay initialization
        if (
          remainingAutoSpins === autoSpinCount &&
          !autoplayInitializedRef.current
        ) {
          autoplayProcessingRef.current = false;
          return;
        }

        // Super-turbo: fast path dispatches counterAnimationFinished instantly,
        // so add SPIN_DELAY to match non-winning timing
        const currentAutoPlaySpeed = store.getState().settings.autoPlaySpeed;
        if (currentAutoPlaySpeed === "super_turbo") {
          const speedConfig = FEATURES_CONFIG.SPIN.AUTO_PLAY_SPEEDS.SUPER_TURBO;
          const winTimeoutId = new TickerTimeout(() => {
            const idx = timeoutsRef.current.indexOf(winTimeoutId);
            if (idx > -1) timeoutsRef.current.splice(idx, 1);
            if (isPresentationHoldingScreen(store.getState())) {
              baseDiag("autoplay win-spin WITHHELD at fire", {
                critical: store.getState().animations?.criticalAnimationPlaying,
                feature: selectAnyFeatureTriggeredOrActive(store.getState()),
              });
              autoplayProcessingRef.current = false;
              return;
            }
            baseDiag("autoplay -> spin (after win)");
            dispatch(decrementRemainingAutoSpins());
            handleSpin();
          }, speedConfig.SPIN_DELAY);
          timeoutsRef.current.push(winTimeoutId);
          return () => {
            winTimeoutId.clear();
            const index = timeoutsRef.current.indexOf(winTimeoutId);
            if (index > -1) timeoutsRef.current.splice(index, 1);
          };
        }

        // For other speeds, trigger immediately — counter animation provides visual pause
        dispatch(decrementRemainingAutoSpins());
        handleSpin();
        return () => {};
      } else {
        autoplayProcessingRef.current = false;
        return () => {};
      }
    }
  }, [
    autoplayState,
    dispatch,
    autoSpinCount,
    handleSpin,
    // Stable ref object from useReelRefs — listed to satisfy the linter, not
    // because it can change. See waitForBoardToSettle in the non-winning path.
    overlayReelRefs,
    isActive,
    reelRefs,
    resetResponsibleGamblingSettings,
  ]);

  /**
   * App Activity Change Effect
   *
   * Handles pause/resume when app/tab becomes inactive/active (browser tab switching).
   *
   * With ticker-based timing, ALL pause/resume is AUTOMATIC:
   * - AppActivityContext sets ticker.speed = 0 → all TickerTimeouts freeze
   * - ReelControllerV2 uses accumulated deltaMS → spin/stop animations freeze
   * - anime.js wind-up/turbo-stop use rAF → also freeze
   * - Spine autoUpdate=true is driven by PIXI.Ticker.shared → also freezes
   *
   * What still needs explicit handling:
   * - Tension sound effects should stop on tab hide (audio doesn't freeze with ticker)
   * - Emergency safety timeout re-arm on resume
   */
  useEffect(() => {
    if (!isActive) {
      pausedByInactivityRef.current = true;



      // Everything else freezes automatically:
      // - Reel spin/stop animations (deltaMS accumulation stops)
      // - TickerTimeouts (stop cascade, autoplay delays)
      // - Tension spines (autoUpdate driven by shared ticker)
      // - anime.js animations (rAF-based)
      return;
    }

    if (!pausedByInactivityRef.current) return;

    // Guard: Prevent multiple rapid calls when tab becomes active
    const now = Date.now();
    if (
      lastTabReturnTimeRef.current &&
      now - lastTabReturnTimeRef.current < 500
    ) {
      return;
    }
    lastTabReturnTimeRef.current = now;

    pausedByInactivityRef.current = false;

    // Everything resumes automatically when ticker.speed returns to 1.
    // Only re-arm emergency timeout as a safety net (wall-clock — intentionally).
    if (isSpinning) {
      const emergencyResumeTimeout = setTimeout(() => {
        const idx = timeoutsRef.current.indexOf(emergencyResumeTimeout);
        if (idx > -1) timeoutsRef.current.splice(idx, 1);
        if (store.getState().spin.isSpinning) {
          handleManualStop();
        }
      }, 15000);
      timeoutsRef.current.push(emergencyResumeTimeout);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, dispatch, stopReels]);

  /**
   * Autoplay Initialization Effect
   *
   * Triggers the first spin when autoplay mode is activated.
   *
   * Conditions:
   * - Autoplay becomes active (autoPlayActive = true)
   * - Remaining spins equals total spins (indicates fresh start)
   * - Not already initialized (autoplayInitializedRef = false)
   *
   * Flow:
   * - Sets initialization flag to prevent duplicate first spins
   * - Decrements remaining spins count
   * - Triggers first spin via handleSpin
   *
   * Reset:
   * - Clears initialization flag when autoplay stops
   *
   * This ensures only ONE first spin is triggered, not multiple spins on rapid activation.
   */
  useEffect(() => {
    if (
      autoPlayActive &&
      remainingAutoSpins === autoSpinCount &&
      !autoplayInitializedRef.current
    ) {
      autoplayInitializedRef.current = true;
      dispatch(decrementRemainingAutoSpins());
      handleSpin();
    } else if (!autoPlayActive && autoplayInitializedRef.current) {
      autoplayInitializedRef.current = false;
    }
  }, [autoPlayActive, autoSpinCount, remainingAutoSpins, dispatch, handleSpin]);

  /**
   * Spin Complete Cleanup Effect
   *
   * Runs when a spin completes (spinComplete = true) to clean up state and handle pending actions.
   *
   * Uses cleanupInProgressRef guard to prevent race conditions from multiple rapid completions.
   *
   * Cleanup tasks:
   * - Clears pending manual stop flag
   * - Clears tension state (pendingTensionRef, tensionReels)
   * - Handles pending turbo activation (only if spacebar not held)
   * - Handles pending turbo stop (restores previous speed if turbo was stopped during spin)
   * - Applies pending autoplay speed changes
   * - Resets spin start time
   * - Clears active tension animations and timestamps
   *
   * Resets cleanupInProgressRef flag after completion via requestAnimationFrame.
   */
  useEffect(() => {
    if (spinComplete && !cleanupInProgressRef.current) {
      cleanupInProgressRef.current = true;

      pendingManualStopRef.current = false;

      // Clean up tension state
      if (pendingTensionRef.current) {
        // Anticipation was active — undim the global overlay now.
        // The last reel's onReelLanded fires via requestAnimationFrame (next frame),
        // but this cleanup runs synchronously on the spinComplete dispatch, so
        // pendingTensionRef is already null by the time onReelLanded reads it.
        // Undim here instead of relying on the callback.
        if (pendingTensionRef.current.isAnticipation) {
          dimReelSetRef.current?.undimReels();
        }
        pendingTensionRef.current = null;
        tensionReels.current = new Set();
      }
      // Stop tension effect and background event animation when spin completes
      if (tensionEffectActiveRef.current) {
        gameContainerRef?.current?.stopTensionEffect?.();
        tensionEffectActiveRef.current = false;

        // Play character "angry" animation when tension ended without a feature trigger.
        // Tension means the player was close to collecting enough coins but missed.
        if (FEATURES_CONFIG.CHARACTER_ANIMATION.ENABLED) {
          const freshState = store.getState();
          const currentRound = freshState.game.currentRound;
          const isHoldAndWinTrigger = detectHoldAndWinTrigger(currentRound);
          const isRoyalCollectTrigger = freshState.featureGame?.royalCollectTriggered;
          const isPotCollectionTrigger = freshState.potCollection?.potCollectionTriggered;

          if (!isHoldAndWinTrigger && !isRoyalCollectTrigger && !isPotCollectionTrigger && !(currentRound?.win > 0)) {
            characterAnimationControllerRef?.current?.playAnimation("angry");
          }
        }
      }

      // Handle pending turbo activation (only if spacebar not held)
      if (
        turboPendingActivationRef.current &&
        !spacebarTurboActive &&
        !autoPlayActive
      ) {
        turboPendingActivationRef.current = false;
      }

      // Handle pending turbo stop
      if (turboPendingStopRef.current && isTurboLike(autoPlaySpeed)) {
        dispatch(setAutoplaySpeed(previousSpeedRef.current));
        turboPendingStopRef.current = false;
        turboDeactivatingRef.current = false;
      }

      // Apply pending autoplay speed change
      if (pendingAutoPlaySpeed !== null) {
        dispatch(applyPendingAutoplaySpeed());
      }

      // Don't reset spinStartTimeRef here - it's needed by API Response Detection Effect
      // It will be reset when the next spin starts in handleSpin()
      activeTensionAnimationsRef.current.clear();
      tensionAnimationStartTimesRef.current.clear();
      turboTensionEjectedRef.current = false;

      // Round-level idle: when enabled, landing animations are holding on their last frame.
      // Now that all reels have stopped, decide whether to trigger idle on all symbols:
      // - Win → win animations will replace held landings (no idle needed)
      // - Autoplay → skip idle, proceed to next spin (hideAllAnimations clears held frames)
      // - Feature trigger → skip idle, transition will handle cleanup
      // - Single spin, no win → show idle on all symbols
      if (FEATURES_CONFIG.SPINE_ANIMATIONS.ROUND_LEVEL_IDLE) {
        const freshState = store.getState();
        const hasWin = freshState.game.currentRound?.win > 0;
        const isAutoplay = freshState.settings.autoPlayActive;
        const isTurbo = isTurboLike(freshState.settings.autoPlaySpeed);
        const isFeatureTrigger = freshState.potCollection?.potCollectionTriggered
          || freshState.featureGame?.royalCollectTriggered;

        if (!hasWin && !isAutoplay && !isTurbo && !isFeatureTrigger) {

          // Unfreeze idle animations that were set up (frozen) during per-reel
          // landing. The texture→spine swap already happened during the reel
          // bounce, so we just need to start the animation movement.
          for (let ri = 0; ri < Settings.REEL_COUNT; ri++) {
            const overlayRef = overlayReelRefs.current?.[ri];
            const overlayCtrl = overlayRef?.getOverlayReelController();
            const reelCtrl = reelRefs.current?.[ri]?.getReelController();
            if (!overlayCtrl) continue;

            // 1. Unfreeze idle-only symbols (frozen during per-reel landing)
            overlayCtrl.unfreezeIdleAnimations();

            // 2. Transition landing-hold symbols to idle (queue, don't interrupt)
            if (!reelCtrl) continue;
            for (let si = 0; si < Settings.SYMBOLS_VISIBLE; si++) {
              const entry = overlayCtrl.activeAnimations?.get(si);
              if (entry?.animationType === "landing") {
                const { hasIdle } = overlayCtrl.getAvailableAnimations(si, entry.symbolId);
                if (hasIdle && entry.spineInstance?.state) {
                  // Queue idle AFTER landing completes — no interruption
                  const idleAnim = overlayCtrl.selectSpineAnimation(
                    entry.spineInstance, "idle", entry.symbolId
                  );
                  if (idleAnim) {
                    entry.spineInstance.state.addAnimation(0, idleAnim, true, 0);
                    overlayCtrl.activeAnimations.set(si, {
                      ...entry,
                      animationType: "idle",
                    });
                  }
                }
              }
            }
          }
        } else {

        }
      }

      requestAnimationFrame(() => {
        cleanupInProgressRef.current = false;
      });
    }
  }, [
    spinComplete,
    autoPlaySpeed,
    pendingAutoPlaySpeed,
    dispatch,
    spacebarTurboActive,
    autoPlayActive,
    gameContainerRef,
    dimReelSetRef,
    characterAnimationControllerRef,
    clearAllTimeouts,
    overlayReelRefs,
    reelRefs,

  ]);

  // ============================================================================
  // COMPONENT LIFECYCLE - UNMOUNT CLEANUP
  // ============================================================================

  /**
   * Component Unmount Cleanup Effect
   *
   * Ensures all timeouts, intervals, and subscriptions are properly cleaned up
   * when the component unmounts to prevent memory leaks.
   *
   * Critical for preventing lag in autoplay mode on Mac and other platforms.
   * Also prevents buildup when performing manual stops during autoplay.
   */
  useEffect(() => {
    // Capture refs at mount to avoid stale references in cleanup
    const gameContainer = gameContainerRef?.current;
    const stoppedReels = stoppedReelsRef.current;
    const activeTensionAnimations = activeTensionAnimationsRef.current;
    const tensionAnimationStartTimes = tensionAnimationStartTimesRef.current;
    const dimmedReels = dimmedReelsRef.current;

    return () => {
      // Clear all tracked timeouts
      clearAllTimeouts();

      // Clear connection timeouts
      clearConnectionTimeouts();
      clearManualStopTimeouts();

      // Stop all sounds
      try {
        soundManager.stop(SOUND_NAME.reel_spin);
      } catch (e) {}

      // Stop tension effect animation
      try {
        gameContainer?.stopTensionEffect?.();
      } catch (e) {}

      // Reset all refs to prevent stale references
      autoplayProcessingRef.current = false;
      autoplayInitializedRef.current = false;
      autoplayStartBalanceRef.current = null;
      pausedByInactivityRef.current = false;
      if (FEATURES_CONFIG.RESPONSIBLE_GAMBLING.ENABLED) {
        responsibleGamblingCheckedRef.current = false;
      }
      cleanupInProgressRef.current = false;
      isSubscribedRef.current = false;
      stopTriggeredRef.current = false;
      pendingManualStopRef.current = false;
      turboPendingStopRef.current = false;
      turboPendingActivationRef.current = false;
      turboDeactivatingRef.current = false;
      tensionEffectActiveRef.current = false;

      // Clear all Sets and Maps
      stoppedReels.clear();
      tensionReels.current.clear();
      activeTensionAnimations.clear();
      tensionAnimationStartTimes.clear();
      dimmedReels.clear();
      suppressLandingAnimationsPerReelRef.current.clear();
      manualStopTargetReelsRef.current.clear();

      // Clear tension state
      pendingTensionRef.current = null;
      currentSpinMethodRef.current = null;
      spinStartTimeRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearAllTimeouts, clearConnectionTimeouts, clearManualStopTimeouts]);

  /**
   * Context Value Memoization
   *
   * Memoizes the context value to prevent unnecessary re-renders of consuming components.
   * Only recreates when dependencies change (handlers, turbo state, or suppression refs).
   *
   * Exposed API:
   * - handleSpin: Main spin initiation function
   * - handleManualStop: Manual stop handler
   * - startSpacebarTurbo: Turbo activation handler
   * - stopSpacebarTurbo: Turbo deactivation handler
   * - spacebarTurboActive: Current turbo state
   * - tensionReels: Set of reels in tension mode
   * - suppressLandingAnimationsRef: Flag to suppress all landing animations (turbo mode)
   * - suppressLandingAnimationsPerReelRef: Set of reel indices to suppress landing animations
   */
  const contextValue = useMemo(() => {
    return {
      handleSpin,
      handleManualStop,
      startSpacebarTurbo,
      stopSpacebarTurbo,
      spacebarTurboActive,
      spacebarTurboActiveRef, // Synchronous mirror — use for race-sensitive checks
      tensionReels,
      pendingTensionRef, // Expose for tension skip in ButtonPlay
      anticipationChainRef, // Expose for anticipation skip in ButtonPlay
      turboTensionEjectedRef, // Expose to block held-spacebar tension skips
      restoreReels, // Expose restore function for error recovery
      // Expose landing suppression flags to hooks/components for animation optimization
      suppressLandingAnimationsRef,
      suppressLandingAnimationsPerReelRef,
    };
  }, [
    handleSpin,
    handleManualStop,
    startSpacebarTurbo,
    stopSpacebarTurbo,
    spacebarTurboActive,
    restoreReels,
    suppressLandingAnimationsRef,
    suppressLandingAnimationsPerReelRef,
  ]);

  return (
    <ReelControlContext.Provider value={contextValue}>
      {children}
    </ReelControlContext.Provider>
  );
};

// Custom hook for consuming ReelControlContext
export const useReelControl = () => {
  const context = useContext(ReelControlContext);
  if (!context) {
    throw new Error("useReelControl must be used within a ReelControlProvider");
  }
  return context;
};
